import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileText,
  HeartPulse,
  Plus,
  ReceiptText,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  UserCog,
  Users,
  Video,
} from "lucide-react";
import { useSelector } from "react-redux";
import { PageNavigation } from "../components/ui/page-navigation";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useSubscription } from "../context/SubscriptionContext";
import MaxSeatModal from "../components/billing/MaxSeatModal";

import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { fetchDoctorsFromHistory } from "../api/callHistory";
import {
  approveUser,
  assignRole,
  createRole,
  deleteRole,
  fetchInvitations,
  fetchPendingApprovals,
  fetchRoles,
  manageRbacOverrides,
  rejectUser,
  revokeInvitation,
  sendInvitation,
  updateRole,
} from "../api/rbac";
import {
  buildPermissionsMap,
  computeEffectivePermissions,
  getBasePermissionsForRole,
  getPermissionLevel,
  normalizeRole,
  PERMISSION_CATALOG,
  SYSTEM_ROLES,
} from "../lib/rbac";
import { useToast } from "../hooks/use-toast";
import { usePermission } from "../hooks/use-permission";

const MANAGED_SYSTEM_ROLES = SYSTEM_ROLES.filter((role) => role !== "SU");
const TAB_OPTIONS = [
  { id: "permissions", label: "Manage Team Access" },
  { id: "roles", label: "Roles & Permissions" },
  { id: "approvals", label: "User Requests" },
  { id: "invites", label: "Invite Users" },
];

const SYSTEM_ROLE_METADATA = {
  Doctor: {
    type: "system",
    baseRoleClonedFrom: "",
    showInRegistration: true,
    skipNpiValidation: false,
  },
  "Nurse Practitioner": {
    type: "system",
    baseRoleClonedFrom: "",
    showInRegistration: true,
    skipNpiValidation: false,
  },
  Staff: {
    type: "system",
    baseRoleClonedFrom: "",
    showInRegistration: true,
    skipNpiValidation: true,
  },
};

const LABEL_ACRONYMS = {
  ehr: "EHR",
  soap: "SOAP",
  rbac: "RBAC",
  npi: "NPI",
  ai: "AI",
};

const formatPermissionLabel = (permissionKey) => {
  const [, action = permissionKey] = permissionKey.split(".");
  return action
    .split("_")
    .map((part) => {
      const normalizedPart = part.toLowerCase();
      if (LABEL_ACRONYMS[normalizedPart]) {
        return LABEL_ACRONYMS[normalizedPart];
      }

      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ");
};

const normalizeSearch = (value) =>
  (value || "").toLowerCase().replace(/\s+/g, " ").trim();

const normalizeClinicName = (value) =>
  (value || "").replace(/\s+/g, " ").trim().toLowerCase();

const getUserRole = (user) => normalizeRole(user.role) || "Unassigned";

const getUserKey = (user) => user.userId || user.id;

const FALLBACK_ACCESS_OPTIONS = {
  none: { id: "none", label: "No Access" },
  read: { id: "read", label: "View Only" },
  write: { id: "write", label: "View & Edit" },
};

const getDisplayName = (user) =>
  user.doctor_name ||
  [user.firstName, user.lastName].filter(Boolean).join(" ") ||
  user.email ||
  user.id;

const getUserEmail = (user) =>
  (user.doctor_email || user.email || user.invitedEmail || user.id || "").trim();

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.split(" ");
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const getOverrideOptionsForPermission = (_permissionKey) => {
  // Always offer all 3 levels as override options for every permission.
  // Defaults per role are enforced by PERMISSION_CATALOG — overrides here
  // are intentionally unrestricted so admins can fine-tune any user's access.
  return ["write", "read", "none"];
};

const isCurrentLoggedInUser = (user, loggedInEmail) => {
  if (!loggedInEmail) {
    return false;
  }

  const userEmail = (user.doctor_email || user.email || user.id || "")
    .trim()
    .toLowerCase();

  return userEmail === loggedInEmail;
};

const asRoleList = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.roles)) {
    return value.roles;
  }

  return [];
};

const getRolePayload = (value, fallback) =>
  value?.role || value?.data || value?.resource || fallback;

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "—";
  }

  return parsedDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDateTimeParts = (value) => {
  if (!value) {
    return { date: "â€”", time: "" };
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return { date: "â€”", time: "" };
  }

  return {
    date: parsedDate.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: parsedDate.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }),
  };
};

const createRoleDraft = (baseRole = "Staff", permissionsByRole = {}) => {
  const normalizedBaseRole = normalizeRole(baseRole) || "Staff";

  return {
    id: "",
    roleName: "",
    description: "",
    type: "custom",
    baseRoleClonedFrom: normalizedBaseRole,
    showInRegistration: true,
    skipNpiValidation: normalizedBaseRole === "Staff",
    permissions: {
      ...(permissionsByRole[normalizedBaseRole] ||
        getBasePermissionsForRole(normalizedBaseRole)),
    },
  };
};

const createRoleDraftFromDoc = (roleDoc) => {
  const normalizedRoleName = normalizeRole(roleDoc?.roleName) || roleDoc?.roleName || "";

  return {
    id: roleDoc?.id || "",
    roleName: normalizedRoleName,
    description: roleDoc?.description || "",
    type: roleDoc?.type || "custom",
    baseRoleClonedFrom: normalizeRole(roleDoc?.baseRoleClonedFrom) || "",
    showInRegistration: Boolean(roleDoc?.showInRegistration),
    skipNpiValidation: Boolean(roleDoc?.skipNpiValidation),
    permissions: buildPermissionsMap(
      roleDoc?.permissions || getBasePermissionsForRole(normalizedRoleName)
    ),
  };
};

const formatApproverName = (value) =>
  value === "system-bootstrap" ? "System-created clinic admin" : value || "—";

const cloneRoleEditorSnapshot = (roleDraft) => ({
  ...roleDraft,
  permissions: {
    ...(roleDraft?.permissions || {}),
  },
});

const isClinicalNpiRole = (roleName) =>
  ["Doctor", "Nurse Practitioner"].includes(normalizeRole(roleName));

const buildRolePermissionsByName = (roleDocs = []) =>
  roleDocs.reduce((acc, roleDoc) => {
    const roleName = normalizeRole(roleDoc?.roleName);
    if (!roleName) {
      return acc;
    }

    acc[roleName] = buildPermissionsMap(roleDoc.permissions);
    return acc;
  }, {});

const buildRoleInventory = (roleDocs = [], users = []) => {
  const roleMap = new Map();

  MANAGED_SYSTEM_ROLES.forEach((roleName) => {
    roleMap.set(roleName, {
      id: roleName.toLowerCase().replace(/\s+/g, "-"),
      roleName,
      description: `${roleName} system role`,
      permissions: getBasePermissionsForRole(roleName),
      ...SYSTEM_ROLE_METADATA[roleName],
      isActive: true,
    });
  });

  roleDocs.forEach((roleDoc) => {
    const roleName = normalizeRole(roleDoc?.roleName);
    if (!roleName) {
      return;
    }

    const existingRole = roleMap.get(roleName) || {};
    const nextRole = {
      ...existingRole,
      ...roleDoc,
      roleName,
      type: roleDoc?.type || existingRole.type || "custom",
      permissions: buildPermissionsMap(
        roleDoc?.permissions ||
          existingRole.permissions ||
          getBasePermissionsForRole(roleName)
      ),
      showInRegistration:
        roleDoc?.showInRegistration ?? existingRole.showInRegistration ?? false,
      skipNpiValidation:
        roleDoc?.skipNpiValidation ?? existingRole.skipNpiValidation ?? false,
      isActive: roleDoc?.isActive ?? existingRole.isActive ?? true,
    };

    roleMap.set(roleName, nextRole);
  });

  const userCounts = users.reduce((acc, user) => {
    const roleName = getUserRole(user);
    acc[roleName] = (acc[roleName] || 0) + 1;
    return acc;
  }, {});

  return Array.from(roleMap.values())
    .map((role) => ({
      ...role,
      userCount: userCounts[role.roleName] || 0,
    }))
    .sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === "system" ? -1 : 1;
      }

      return a.roleName.localeCompare(b.roleName, undefined, {
        sensitivity: "base",
      });
    });
};

const PermissionSelector = ({
  value,
  onChange,
  options = ["write", "read", "none"],
  disabled = false,
}) => {
  const levels = [
    { id: "write", label: "View & Edit" },
    { id: "read", label: "View Only" },
    { id: "none", label: "No Access" },
  ];

  const availableLevels = levels.filter(l => options.includes(l.id));

  return (
    <div className="flex flex-wrap items-center gap-x-1 lg:gap-x-2 py-1">
      {availableLevels.map((level) => {
        const isActive = value === level.id;
        
        return (
          <div
            key={level.id}
            role="radio"
            aria-checked={isActive}
            tabIndex={disabled ? -1 : 0}
            className={`
              flex items-center gap-2.5 rounded-lg border px-3 py-2 transition-all duration-150 select-none
              ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer group hover:bg-white hover:shadow-md hover:border-blue-200"}
              ${isActive
                ? "bg-blue-50 border-blue-400 shadow-sm ring-1 ring-blue-100"
                : "bg-white border-gray-200"
              }
            `}
            onClick={() => {
              if (disabled) return;
              onChange(isActive ? "" : level.id); 
            }}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !disabled) {
                onChange(isActive ? "" : level.id);
              }
            }}
          >
            {/* Visual checkbox indicator - Consistent small size */}
            <div className={`
              flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border-2 transition-colors
              ${isActive ? "bg-blue-600 border-blue-600" : "bg-white border-gray-400 group-hover:border-blue-400"}
            `}>
              {isActive && (
                <svg viewBox="0 0 10 8" className="h-2.5 w-2.5 fill-white">
                  <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              )}
            </div>
            <span className={`text-[12.5px] font-bold tracking-tight transition-colors ${
              isActive ? "text-blue-700" : "text-gray-600 group-hover:text-gray-900"
            }`}>
              {level.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const WORKFLOW_PERMISSION_GROUPS = {
  appointments: [
    "dashboard.view_appointments",
    "dashboard.todays_schedule",
    "dashboard.status_overview",
    "dashboard.provider_workload",
    "appointments.select_providers",
    "appointments.add",
    "appointments.modify",
    "appointments.delete",
    "appointments.patient_reports",
  ],
  videoVisits: [
    "dashboard.start_video_call",
    "appointments.join_call",
    "video_call.upcoming",
    "video_call.start",
    "video_call.add",
    "patients.join_call",
  ],
  callHistory: ["video_call.history", "post_call.view_all", "patients.previous_calls"],
  patientRecords: [
    "patients.info",
    "patients.clinical_summary",
    "patients.upcoming_appointment",
  ],
  postCallDocumentation: [
    "appointments.post_call_doc",
    "video_call.post_call_doc",
    "patients.post_call_doc",
    "post_call.view_all",
    "post_call.edit_soap_notes",
    "post_call.edit_billing_codes",
    "post_call.add_doctor_notes",
    "post_call.edit_doctor_notes",
    "post_call.add_feedback",
    "post_call.edit_feedback",
  ],
  billing: [
    "reports.billing_analytics",
    "reports.billing_history",
    "reports.estimated_billing",
    "settings.payment_billing",
  ],
  valueBasedCare: ["chatbot.access"],
  admin: ["admin.manage_rbac"],
  settings: ["settings.ehr_integration", "settings.payment_billing"],
};

const WORKFLOW_DEFINITIONS = [
  {
    id: "appointments",
    title: "Appointment Management",
    description: "Schedule, update, and review appointments for the clinic.",
    icon: CalendarDays,
    options: [
      { id: "none", label: "No Access" },
      { id: "read", label: "View Only" },
      { id: "manage", label: "Create & Manage" },
    ],
    mapToPermissions: (level) =>
      Object.fromEntries(
        WORKFLOW_PERMISSION_GROUPS.appointments.map((key) => [
          key,
          level === "none" ? "none" : level === "read" ? "read" : "write",
        ])
      ),
    inferLevel: (permissions) => {
      const hasWrite = WORKFLOW_PERMISSION_GROUPS.appointments.some(
        (key) => getPermissionLevel(permissions, key) === "write"
      );
      const hasRead = WORKFLOW_PERMISSION_GROUPS.appointments.some(
        (key) => getPermissionLevel(permissions, key) !== "none"
      );
      return hasWrite ? "manage" : hasRead ? "read" : "none";
    },
  },
  {
    id: "videoVisits",
    title: "Video Visits",
    description: "Join, start, and conduct video calls from clinical workflows.",
    icon: Video,
    options: [
      { id: "none", label: "No Access" },
      { id: "assigned", label: "Join Assigned Calls Only" },
      { id: "any", label: "Join Any Provider's Calls" },
      { id: "conduct", label: "Create & Conduct Calls" },
    ],
    mapToPermissions: (level) => ({
      "dashboard.start_video_call": level === "conduct" ? "write" : "none",
      "appointments.join_call": level === "none" ? "none" : "write",
      "video_call.upcoming": level === "none" ? "none" : level === "assigned" ? "read" : "write",
      "video_call.start": level === "conduct" ? "write" : "none",
      "video_call.add": level === "conduct" ? "write" : "none",
      "patients.join_call": level === "none" ? "none" : "write",
    }),
    inferLevel: (permissions) => {
      if (
        getPermissionLevel(permissions, "video_call.start") === "write" ||
        getPermissionLevel(permissions, "video_call.add") === "write" ||
        getPermissionLevel(permissions, "dashboard.start_video_call") === "write"
      ) {
        return "conduct";
      }
      if (getPermissionLevel(permissions, "video_call.upcoming") === "write") {
        return "any";
      }
      if (
        getPermissionLevel(permissions, "appointments.join_call") !== "none" ||
        getPermissionLevel(permissions, "patients.join_call") !== "none"
      ) {
        return "assigned";
      }
      return "none";
    },
  },
  {
    id: "callHistory",
    title: "Call History",
    description: "Review completed visit history for the user or clinic providers.",
    icon: ClipboardList,
    options: [
      { id: "none", label: "No Access" },
      { id: "own", label: "Own Calls Only" },
      { id: "all", label: "All Provider Calls" },
    ],
    mapToPermissions: (level) => ({
      "video_call.history": level === "none" ? "none" : level === "own" ? "read" : "write",
      "post_call.view_all": level === "all" ? "write" : level === "own" ? "read" : "none",
      "patients.previous_calls": level === "none" ? "none" : level === "own" ? "read" : "write",
    }),
    inferLevel: (permissions) => {
      if (getPermissionLevel(permissions, "video_call.history") === "write") {
        return "all";
      }
      return getPermissionLevel(permissions, "video_call.history") === "read"
        ? "own"
        : "none";
    },
  },
  {
    id: "patientRecords",
    title: "Patient Records",
    description: "Open patient profiles, summaries, and appointment context.",
    icon: FileText,
    options: [
      { id: "none", label: "No Access" },
      { id: "read", label: "View Only" },
      { id: "write", label: "View & Edit" },
    ],
    mapToPermissions: (level) =>
      Object.fromEntries(
        WORKFLOW_PERMISSION_GROUPS.patientRecords.map((key) => [
          key,
          level === "none" ? "none" : level,
        ])
      ),
    inferLevel: (permissions) => {
      const hasWrite = WORKFLOW_PERMISSION_GROUPS.patientRecords.some(
        (key) => getPermissionLevel(permissions, key) === "write"
      );
      const hasRead = WORKFLOW_PERMISSION_GROUPS.patientRecords.some(
        (key) => getPermissionLevel(permissions, key) !== "none"
      );
      return hasWrite ? "write" : hasRead ? "read" : "none";
    },
  },
  {
    id: "postCallDocumentation",
    title: "Practice Intelligence",
    description: "View, edit, and submit clinical documentation after visits.",
    icon: ReceiptText,
    options: [
      { id: "none", label: "No Access" },
      { id: "read", label: "View Only" },
      { id: "edit", label: "Edit Documentation" },
      { id: "submit", label: "Edit & Submit to EHR" },
    ],
    mapToPermissions: (level) => {
      const viewLevel = level === "none" ? "none" : "read";
      const editLevel = level === "edit" || level === "submit" ? "write" : viewLevel;
      return {
        "appointments.post_call_doc": viewLevel,
        "video_call.post_call_doc": viewLevel,
        "patients.post_call_doc": viewLevel,
        "post_call.view_all": viewLevel,
        "post_call.edit_soap_notes": editLevel,
        "post_call.edit_billing_codes": editLevel,
        "post_call.add_doctor_notes": editLevel,
        "post_call.edit_doctor_notes": editLevel,
        "post_call.add_feedback": editLevel,
        "post_call.edit_feedback": level === "submit" ? "write" : level === "none" ? "none" : "read",
      };
    },
    inferLevel: (permissions) => {
      if (
        getPermissionLevel(permissions, "post_call.edit_feedback") === "write"
      ) {
        return "submit";
      }
      if (
        getPermissionLevel(permissions, "post_call.edit_soap_notes") === "write" ||
        getPermissionLevel(permissions, "post_call.edit_billing_codes") === "write" ||
        getPermissionLevel(permissions, "post_call.edit_doctor_notes") === "write" ||
        getPermissionLevel(permissions, "post_call.add_feedback") === "write"
      ) {
        return "edit";
      }
      return WORKFLOW_PERMISSION_GROUPS.postCallDocumentation.some(
        (key) => getPermissionLevel(permissions, key) !== "none"
      )
        ? "read"
        : "none";
    },
  },
  {
    id: "billing",
    title: "Billing & Payments",
    description: "Review financial reporting and payment-related admin settings.",
    icon: CreditCard,
    options: [
      { id: "none", label: "No Access" },
      { id: "read", label: "View Only" },
      { id: "manage", label: "Manage Billing" },
    ],
    mapToPermissions: (level) =>
      Object.fromEntries(
        WORKFLOW_PERMISSION_GROUPS.billing.map((key) => [
          key,
          level === "none" ? "none" : level === "read" ? "read" : "write",
        ])
      ),
    inferLevel: (permissions) => {
      const hasWrite = WORKFLOW_PERMISSION_GROUPS.billing.some(
        (key) => getPermissionLevel(permissions, key) === "write"
      );
      const hasRead = WORKFLOW_PERMISSION_GROUPS.billing.some(
        (key) => getPermissionLevel(permissions, key) !== "none"
      );
      return hasWrite ? "manage" : hasRead ? "read" : "none";
    },
  },
  {
    id: "valueBasedCare",
    title: "Chatbot",
    description: "Access the clinical AI chatbot/support assistant.",
    icon: HeartPulse,
    options: [
      { id: "none", label: "No Access" },
      { id: "read", label: "View Only" },
    ],
    mapToPermissions: (level) => ({
      "chatbot.access": level === "none" ? "none" : "read",
    }),
    inferLevel: (permissions) =>
      getPermissionLevel(permissions, "chatbot.access") === "none" ? "none" : "read",
  },
  {
    id: "admin",
    title: "Admin Management",
    description: "Manage users, role templates, approvals, invitations, and access.",
    icon: UserCog,
    options: [
      { id: "none", label: "No Access" },
      { id: "manage", label: "Manage Users & Roles" },
    ],
    mapToPermissions: (level) => ({
      "admin.manage_rbac": level === "manage" ? "write" : "none",
    }),
    inferLevel: (permissions) =>
      getPermissionLevel(permissions, "admin.manage_rbac") === "write" ? "manage" : "none",
  },
  {
    id: "settings",
    title: "Settings",
    description: "Keep profile access available while controlling clinic settings.",
    icon: Settings,
    options: [
      { id: "basic", label: "Basic Profile Only" },
      { id: "clinic", label: "Profile + Clinic Settings" },
      { id: "full", label: "Full Settings" },
    ],
    mapToPermissions: (level) => ({
      "settings.ehr_integration": level === "basic" ? "read" : "write",
      "settings.payment_billing": level === "full" ? "write" : level === "clinic" ? "read" : "none",
    }),
    inferLevel: (permissions) => {
      if (getPermissionLevel(permissions, "settings.payment_billing") === "write") {
        return "full";
      }
      if (
        getPermissionLevel(permissions, "settings.ehr_integration") === "write" ||
        getPermissionLevel(permissions, "settings.payment_billing") === "read"
      ) {
        return "clinic";
      }
      return "basic";
    },
  },
];

const CUSTOM_WORKFLOW_ACCESS_VALUE = "custom";
const CUSTOM_WORKFLOW_ACCESS_LABEL = "Custom Access";
const CUSTOM_WORKFLOW_ACCESS_HELPER_TEXT =
  "Some permissions are customized. Open Advanced Permissions to review.";

const AccessDefaultSummary = ({
  defaultLevel,
  currentLevel,
  isCustom,
}) => (
  <div className="rounded-lg border border-blue-100 bg-white p-3 text-sm text-gray-700 shadow-sm">
    {isCustom ? (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="border-blue-200 bg-blue-50 text-[11px] font-semibold text-blue-700"
          >
            {CUSTOM_WORKFLOW_ACCESS_LABEL}
          </Badge>
          <span className="text-sm font-semibold text-gray-900">
            Some permissions are customized.
          </span>
        </div>
        <p className="text-xs font-medium text-gray-500">
          Open Advanced Permissions to review.
        </p>
        <div className="grid gap-3 rounded-md bg-gray-50 p-3 sm:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              This role normally allows:
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              {defaultLevel}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Current access:
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              {currentLevel}
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          This role normally allows:
        </div>
        <div className="mt-1 text-sm font-semibold text-gray-900">
          {currentLevel}
        </div>
      </div>
    )}
  </div>
);

const getWorkflowPermissionKeysFromCatalog = (workflowId) =>
  (WORKFLOW_PERMISSION_GROUPS[workflowId] || Object.keys(PERMISSION_CATALOG)).filter(
    (permissionKey) =>
      Object.prototype.hasOwnProperty.call(PERMISSION_CATALOG, permissionKey)
  );

const getWorkflowChildPermissionLevel = (level) => {
  if (level === "none") {
    return "none";
  }

  if (level === "read" || level === "assigned" || level === "own" || level === "basic") {
    return "read";
  }

  return "write";
};

const getWorkflowPermissionUpdates = (workflow, level) => {
  if (typeof workflow.mapToPermissions === "function") {
    return Object.fromEntries(
      Object.entries(workflow.mapToPermissions(level)).filter(([permissionKey]) =>
        Object.prototype.hasOwnProperty.call(PERMISSION_CATALOG, permissionKey)
      )
    );
  }

  const childLevel = getWorkflowChildPermissionLevel(level);
  const permissionKeys = getWorkflowPermissionKeysFromCatalog(workflow.id);
  const updates = {};

  permissionKeys.forEach((permissionKey) => {
    updates[permissionKey] = childLevel;
  });

  return updates;
};

const getWorkflowAccessState = (workflow, permissions = {}) => {
  const permissionKeys = getWorkflowPermissionKeysFromCatalog(workflow.id);

  if (permissionKeys.length === 0) {
    return {
      value: workflow.options[0]?.id || "",
      isCustom: false,
    };
  }

  const matchingOption = [...workflow.options].reverse().find((option) => {
    const expectedUpdates = getWorkflowPermissionUpdates(workflow, option.id);

    return permissionKeys.every(
      (permissionKey) =>
        getPermissionLevel(permissions, permissionKey) ===
        expectedUpdates[permissionKey]
    );
  });

  if (matchingOption) {
    return {
      value: matchingOption.id,
      isCustom: false,
    };
  }

  const matchingFallbackLevel = ["write", "read", "none"].find((level) =>
    permissionKeys.every(
      (permissionKey) => getPermissionLevel(permissions, permissionKey) === level
    )
  );

  if (matchingFallbackLevel) {
    return {
      value: matchingFallbackLevel,
      isCustom: false,
    };
  }

  return {
    value: CUSTOM_WORKFLOW_ACCESS_VALUE,
    isCustom: true,
  };
};

const getWorkflowAccessLabel = (workflow, value) => {
  if (value === CUSTOM_WORKFLOW_ACCESS_VALUE) {
    return CUSTOM_WORKFLOW_ACCESS_LABEL;
  }

  return (
    workflow.options.find((option) => option.id === value)?.label ||
    FALLBACK_ACCESS_OPTIONS[value]?.label ||
    value
  );
};

const EHR_ADVANCED_CONTROLS = [
  { id: "soap", label: "SOAP Notes", permissionKey: "post_call.edit_soap_notes" },
  { id: "recommendations", label: "Recommendations", permissionKey: "post_call.add_feedback" },
  { id: "billingCodes", label: "Billing Codes", permissionKey: "post_call.edit_billing_codes" },
];

const EHR_CONTROL_OPTIONS = [
  { id: "none", label: "No Access" },
  { id: "read", label: "View Only" },
  { id: "write", label: "View & Edit" },
  { id: "submit", label: "View, Edit & Submit to EHR" },
];

const POST_CALL_INCLUDES = [
  "Summary",
  "Transcript",
  "SOAP Notes",
  "Recommendations",
  "Billing Codes",
  "Doctor Notes",
  "Emotional Intelligence",
];

const ACCESS_LEGEND_ITEMS = [
  {
    label: "No Access",
    description: "Hides the workflow and blocks related actions.",
    icon: ShieldCheck,
  },
  {
    label: "View Only",
    description: "Lets the user review information without changing it.",
    icon: FileText,
  },
  {
    label: "Create & Manage",
    description: "Allows scheduling, edits, and workflow updates.",
    icon: CalendarDays,
  },
  {
    label: "Join Assigned Calls",
    description: "Allows joining visits tied to that user's care work.",
    icon: Video,
  },
  {
    label: "Own Calls Only",
    description: "Limits call history to the user's completed visits.",
    icon: ClipboardList,
  },
  {
    label: "All Provider Calls",
    description: "Opens clinic-wide provider call history.",
    icon: Users,
  },
  {
    label: "Edit Documentation",
    description: "Allows clinical note updates where supported.",
    icon: ReceiptText,
  },
  {
    label: "Submit to EHR",
    description: "Allows approved EHR posting controls for supported note types.",
    icon: Check,
  },
];

const InlineAccessPicker = ({
  id,
  value,
  options,
  disabled = false,
  openPickerId,
  setOpenPickerId,
  onChange,
}) => {
  const isOpen = openPickerId === id;
  const selectedOption =
    value === CUSTOM_WORKFLOW_ACCESS_VALUE
      ? { id: CUSTOM_WORKFLOW_ACCESS_VALUE, label: CUSTOM_WORKFLOW_ACCESS_LABEL }
      : options.find((option) => option.id === value) ||
        FALLBACK_ACCESS_OPTIONS[value] ||
        options[0];

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => {
          if (!disabled) {
            setOpenPickerId(isOpen ? "" : id);
          }
        }}
        disabled={disabled}
        className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-left text-sm font-medium text-gray-800 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-50/30 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
      >
        <span>{selectedOption?.label || "Choose access"}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {isOpen ? (
        <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
          {options.map((option) => {
            const isSelected = option.id === value;

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onChange(option.id);
                  setOpenPickerId("");
                }}
                className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                  isSelected
                    ? "border-blue-200 bg-blue-50 text-blue-900"
                    : "border-gray-100 bg-white text-gray-700 hover:border-blue-200 hover:bg-blue-50/60"
                }`}
              >
                <span className="font-medium">{option.label}</span>
                {isSelected ? <Check className="h-4 w-4 text-blue-600" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const RequestNameCell = ({ user }) => {
  const displayName = getDisplayName(user);

  return (
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-700 ring-1 ring-blue-100">
        {getInitials(displayName)}
      </div>
      <div className="min-w-0 truncate font-semibold text-gray-900" title={displayName}>
        {displayName}
      </div>
    </div>
  );
};

const RequestRoleBadge = ({ role }) => (
  <span
    className="inline-flex max-w-full items-center rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700"
    title={role}
  >
    <span className="truncate">{role}</span>
  </span>
);

const RequestDateCell = ({ value }) => {
  const { date, time } = formatDateTimeParts(value);

  return (
    <div className="leading-tight">
      <div className="whitespace-nowrap text-gray-700">{date}</div>
      {time ? (
        <div className="mt-1 whitespace-nowrap text-xs text-gray-500">{time}</div>
      ) : null}
    </div>
  );
};

const TruncatedRequestCell = ({ value, className = "" }) => {
  const displayValue = value || "â€”";

  return (
    <div className={`min-w-0 truncate text-gray-600 ${className}`} title={displayValue}>
      {displayValue}
    </div>
  );
};

function RBACManagement() {
  const loggedInClinicName = useSelector((state) => state.me?.me?.clinicName || "");
  const loggedInEmail = useSelector((state) =>
    (state.me?.me?.email || "").trim().toLowerCase()
  );
  const canWriteAdminSettings = usePermission("admin.manage_rbac", "write");
  const [activeTab, setActiveTab] = useState("permissions");

  useEffect(() => {
    const handler = (event) => {
      if (event.detail?.page === "admin-rbac" && event.detail?.tab) {
        setActiveTab(event.detail.tab);
      }
    };
    window.addEventListener("tour-tab-change", handler);
    return () => window.removeEventListener("tour-tab-change", handler);
  }, []);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [requestSearch, setRequestSearch] = useState("");
  const [requestRoleFilter, setRequestRoleFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [rolesError, setRolesError] = useState("");
  const [approvalsError, setApprovalsError] = useState("");
  const [invitationsError, setInvitationsError] = useState("");
  const [refreshingApprovals, setRefreshingApprovals] = useState(false);
  const [workflowDrafts, setWorkflowDrafts] = useState({});
  const [expandedWorkflows, setExpandedWorkflows] = useState({
    postCallDocumentation: true,
  });
  const [advancedEhrOpen, setAdvancedEhrOpen] = useState(false);
  const [openAccessPickerId, setOpenAccessPickerId] = useState("");
  const [selectedAssignmentRole, setSelectedAssignmentRole] = useState("");
  const [assigningRole, setAssigningRole] = useState(false);
  const [savingPermissionKey, setSavingPermissionKey] = useState("");
  const [savingRole, setSavingRole] = useState(false);
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [approvingUserId, setApprovingUserId] = useState("");
  const [rejectingUserId, setRejectingUserId] = useState("");
  const [revokingInvitationId, setRevokingInvitationId] = useState("");
  const [deletingRoleId, setDeletingRoleId] = useState("");
  const [rolePendingDelete, setRolePendingDelete] = useState(null);
  const [approvalPendingReject, setApprovalPendingReject] = useState(null);
  const [deleteReplacementRole, setDeleteReplacementRole] = useState("");
  const [editorMode, setEditorMode] = useState("");
  const [roleEditorScrollRequest, setRoleEditorScrollRequest] = useState(0);
  const roleEditorRef = useRef(null);
  const [invitationForm, setInvitationForm] = useState({
    email: "",
    roleName: "",
  });
  const [roleForm, setRoleForm] = useState(() => createRoleDraft());
  const [roleEditorBaseline, setRoleEditorBaseline] = useState(() =>
    cloneRoleEditorSnapshot(createRoleDraft())
  );
  const [expandedRoleWorkflows, setExpandedRoleWorkflows] = useState({});
  const { toast } = useToast();
  const subscription = useSubscription();
  const [showMaxSeatModal, setShowMaxSeatModal] = useState(false);

  const showReadOnlyToast = () => {
    toast({
      title: "Read-only access",
      description:
        "You can review admin settings, but only users with write access can make changes.",
    });
  };

  useEffect(() => {
    document.title = "Admin Settings - Seismic Connect";
  }, []);

  const roleInventory = useMemo(() => buildRoleInventory(roles, users), [roles, users]);

  const rolePermissionsByName = useMemo(
    () =>
      roleInventory.reduce((acc, role) => {
        acc[role.roleName] = buildPermissionsMap(role.permissions);
        return acc;
      }, {}),
    [roleInventory]
  );

  const availableRoleOptions = useMemo(
    () =>
      roleInventory
        .filter((role) => role.isActive !== false)
        .map((role) => role.roleName),
    [roleInventory]
  );

  const deleteReplacementRoleOptions = useMemo(() => {
    const pendingRoleName = normalizeRole(rolePendingDelete?.roleName);

    return roleInventory
      .filter((role) => {
        if (role.isActive === false) {
          return false;
        }

        return normalizeRole(role.roleName) !== pendingRoleName;
      })
      .map((role) => role.roleName);
  }, [roleInventory, rolePendingDelete]);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setLoadError("");
      setRolesError("");
      setApprovalsError("");
      setInvitationsError("");

      try {
        const [
          usersResult,
          rolesResult,
          approvalsResult,
          invitationsResult,
        ] = await Promise.allSettled([
          fetchDoctorsFromHistory(loggedInClinicName || undefined),
          fetchRoles(loggedInClinicName || ""),
          fetchPendingApprovals(),
          fetchInvitations(),
        ]);

        if (usersResult.status !== "fulfilled") {
          throw usersResult.reason;
        }

        const loadedRoles =
          rolesResult.status === "fulfilled" ? asRoleList(rolesResult.value) : [];

        if (rolesResult.status !== "fulfilled") {
          setRolesError(
            rolesResult.reason?.message ||
              "Role endpoints are not available yet. Custom roles will show once the backend is deployed."
          );
        }

        if (approvalsResult.status !== "fulfilled") {
          setApprovalsError(
            approvalsResult.reason?.message ||
              "Pending approvals could not be loaded right now."
          );
        }

        if (invitationsResult.status !== "fulfilled") {
          setInvitationsError(
            invitationsResult.reason?.message ||
              "Invitations could not be loaded right now."
          );
        }

        const normalizedClinicName = normalizeClinicName(loggedInClinicName);
        const rolePermissions = buildRolePermissionsByName(loadedRoles);

        const normalizedUsers = (Array.isArray(usersResult.value) ? usersResult.value : [])
          .filter((user) => {
            if (normalizedClinicName) {
              return normalizeClinicName(user.clinicName) === normalizedClinicName;
            }

            const userEmail = (user.doctor_email || user.email || user.id || "")
              .trim()
              .toLowerCase();
            return userEmail === loggedInEmail;
          })
          .map((user) => {
            const roleName = getUserRole(user);

            return {
              ...user,
              role: roleName,
              effectivePermissions: computeEffectivePermissions(
                roleName,
                user.customPermissions,
                rolePermissions[roleName] || null
              ),
            };
          })
          .sort((a, b) =>
            getDisplayName(a).localeCompare(getDisplayName(b), undefined, {
              sensitivity: "base",
            })
          );

        setRoles(loadedRoles);
        setUsers(normalizedUsers);
        setPendingApprovals(
          approvalsResult.status === "fulfilled" && Array.isArray(approvalsResult.value)
            ? approvalsResult.value
            : []
        );
        setInvitations(
          invitationsResult.status === "fulfilled" && Array.isArray(invitationsResult.value)
            ? invitationsResult.value
            : []
        );
      } catch (error) {
        setLoadError(error?.message || "Failed to load users for RBAC management.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [loggedInClinicName, loggedInEmail]);

  const filteredUsers = useMemo(() => {
    const manageableUsers = users.filter(
      (user) => !isCurrentLoggedInUser(user, loggedInEmail)
    );
    const query = normalizeSearch(search);
    return manageableUsers.filter((user) => {
      if (roleFilter && normalizeRole(user.role) !== normalizeRole(roleFilter)) {
        return false;
      }

      const haystack = [getDisplayName(user), user.email, user.role]
        .filter(Boolean)
        .join(" ");

      return !query || normalizeSearch(haystack).includes(query);
    });
  }, [loggedInEmail, roleFilter, search, users]);

  const groupedUsers = useMemo(() => {
    return filteredUsers.reduce((acc, user) => {
      const key = user.role;
      if (!acc[key]) {
        acc[key] = {
          role: user.role,
          users: [],
        };
      }

      acc[key].users.push(user);
      return acc;
    }, {});
  }, [filteredUsers]);

  const filteredUserRequests = useMemo(() => {
    const applyRequestFilters = (entries = []) => {
      const query = normalizeSearch(requestSearch);

      return entries.filter((user) => {
        if (requestRoleFilter && normalizeRole(user.role) !== normalizeRole(requestRoleFilter)) {
          return false;
        }

        const haystack = [
          getDisplayName(user),
          getUserEmail(user),
          user.role,
          user.approvedBy,
          user.rejectedBy,
        ]
          .filter(Boolean)
          .join(" ");

        return !query || normalizeSearch(haystack).includes(query);
      });
    };

    const pending = applyRequestFilters(pendingApprovals);
    const approved = applyRequestFilters(
      users.filter((user) => user.approvalStatus === "approved")
    );
    const rejected = applyRequestFilters(
      users.filter((user) => user.approvalStatus === "rejected")
    );

    return {
      pending,
      approved,
      rejected,
    };
  }, [pendingApprovals, requestRoleFilter, requestSearch, users]);

  const selectedUsers = useMemo(
    () =>
      users.filter(
        (user) =>
          selectedUserIds.includes(getUserKey(user)) &&
          !isCurrentLoggedInUser(user, loggedInEmail)
      ),
    [loggedInEmail, selectedUserIds, users]
  );

  const selectedUser = selectedUsers[0] || null;

  const selectedUserRolePermissions = useMemo(() => {
    if (!selectedUser) {
      return {};
    }

    return (
      rolePermissionsByName[selectedUser.role] ||
      getBasePermissionsForRole(selectedUser.role)
    );
  }, [rolePermissionsByName, selectedUser]);

  const hasUnsavedWorkflowChanges = useMemo(
    () => Object.keys(workflowDrafts).length > 0,
    [workflowDrafts]
  );

  const customRoleCount = useMemo(
    () => roleInventory.filter((role) => role.type !== "system" && role.isActive !== false).length,
    [roleInventory]
  );

  const usersOnCustomRoles = useMemo(
    () =>
      users.filter((user) => !MANAGED_SYSTEM_ROLES.includes(normalizeRole(user.role))).length,
    [users]
  );

  useEffect(() => {
    setSelectedUserIds((current) =>
      current.filter((userId) =>
        users.some(
          (user) =>
            getUserKey(user) === userId &&
            !isCurrentLoggedInUser(user, loggedInEmail)
        )
      )
    );
  }, [loggedInEmail, users]);

  useEffect(() => {
    if (!hasUnsavedWorkflowChanges) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedWorkflowChanges]);

  useEffect(() => {
    if (!roleEditorScrollRequest || !editorMode) {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      roleEditorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [editorMode, roleEditorScrollRequest]);

  const toggleUser = (userId) => {
    setSelectedUserIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId]
    );
  };

  const selectPermissionUser = (userId) => {
    if (hasUnsavedWorkflowChanges) {
      const shouldContinue = window.confirm(
        "You have unsaved changes. Discard them and switch users?"
      );
      if (!shouldContinue) {
        return;
      }
      setWorkflowDrafts({});
    }

    setSelectedUserIds(userId ? [userId] : []);
  };

  const handleTabChange = (tabId) => {
    if (tabId !== activeTab && hasUnsavedWorkflowChanges) {
      const shouldContinue = window.confirm(
        "You have unsaved changes. Discard them and leave this tab?"
      );
      if (!shouldContinue) {
        return;
      }
      setWorkflowDrafts({});
    }

    setActiveTab(tabId);
  };

  const toggleRoleUsers = (roleUserIds) => {
    setSelectedUserIds((current) => {
      const normalizedCurrent = new Set(current);
      const everySelected = roleUserIds.every((id) => normalizedCurrent.has(id));

      if (everySelected) {
        return current.filter((id) => !roleUserIds.includes(id));
      }

      roleUserIds.forEach((id) => normalizedCurrent.add(id));
      return Array.from(normalizedCurrent);
    });
  };

  const toggleWorkflow = (workflowId) => {
    setExpandedWorkflows((current) => ({
      ...current,
      [workflowId]: !current[workflowId],
    }));
  };

  const refreshUsersSnapshot = async () => {
    try {
      const fetchedUsers = await fetchDoctorsFromHistory(loggedInClinicName || undefined);
      const normalizedClinicName = normalizeClinicName(loggedInClinicName);
      const rolePermissions = buildRolePermissionsByName(roles);

      const normalizedUsers = (Array.isArray(fetchedUsers) ? fetchedUsers : [])
        .filter((user) => {
          if (normalizedClinicName) {
            return normalizeClinicName(user.clinicName) === normalizedClinicName;
          }

          const userEmail = (user.doctor_email || user.email || user.id || "")
            .trim()
            .toLowerCase();
          return userEmail === loggedInEmail;
        })
        .map((user) => {
          const roleName = getUserRole(user);

          return {
            ...user,
            role: roleName,
            effectivePermissions: computeEffectivePermissions(
              roleName,
              user.customPermissions,
              rolePermissions[roleName] || null
            ),
          };
        })
        .sort((a, b) =>
          getDisplayName(a).localeCompare(getDisplayName(b), undefined, {
            sensitivity: "base",
          })
        );

      setUsers(normalizedUsers);
    } catch (error) {
      toast({
        title: "Failed to refresh users",
        description:
          error?.message || "User lists could not be refreshed right now.",
      });
    }
  };

  const getWorkflowOriginalValue = (workflow) => {
    if (!workflow) {
      return "";
    }

    if (!selectedUser) {
      return workflow.options[0]?.id || "";
    }

    return getWorkflowAccessState(
      workflow,
      selectedUser.effectivePermissions
    ).value;
  };

  const getDraftedWorkflowPermissions = () => {
    if (!selectedUser) {
      return {};
    }

    const workflowOverrides = WORKFLOW_DEFINITIONS.reduce((acc, workflow) => {
      const draftValue = workflowDrafts[workflow.id];
      if (!draftValue) {
        return acc;
      }

      return {
        ...acc,
        ...getWorkflowPermissionUpdates(workflow, draftValue),
      };
    }, {});

    const advancedOverrides = EHR_ADVANCED_CONTROLS.reduce((acc, control) => {
      const draftValue = workflowDrafts[`ehr.${control.permissionKey}`];
      if (!draftValue) {
        return acc;
      }

      acc[control.permissionKey] = draftValue === "submit" ? "write" : draftValue;
      return acc;
    }, {});

    return {
      ...selectedUser.effectivePermissions,
      ...workflowOverrides,
      ...advancedOverrides,
    };
  };

  const getWorkflowValue = (workflow) =>
    getWorkflowAccessState(workflow, getDraftedWorkflowPermissions()).value;

  const getAdvancedEhrOriginalValue = (permissionKey) => {
    const level = getPermissionLevel(selectedUser?.effectivePermissions, permissionKey);
    if (
      selectedUser &&
      getPermissionLevel(selectedUser.effectivePermissions, "post_call.edit_feedback") === "write" &&
      ["post_call.edit_soap_notes", "post_call.add_feedback", "post_call.edit_billing_codes"].includes(permissionKey)
    ) {
      return "submit";
    }

    return level;
  };

  const getAdvancedEhrValue = (permissionKey) => {
    const draftKey = `ehr.${permissionKey}`;
    if (Object.prototype.hasOwnProperty.call(workflowDrafts, draftKey)) {
      return workflowDrafts[draftKey];
    }

    const draftedPermissions = getDraftedWorkflowPermissions();
    const level = getPermissionLevel(draftedPermissions, permissionKey);
    const workflowValue =
      workflowDrafts.postCallDocumentation ||
      getWorkflowAccessState(
        WORKFLOW_DEFINITIONS.find(
          (workflow) => workflow.id === "postCallDocumentation"
        ),
        draftedPermissions
      ).value;

    return level === "write" && workflowValue === "submit" ? "submit" : level;
  };

  const handleWorkflowDraftChange = (workflowId, value) => {
    if (!canWriteAdminSettings) {
      showReadOnlyToast();
      return;
    }

    const originalValue = workflowId.startsWith("ehr.")
      ? getAdvancedEhrOriginalValue(workflowId.replace("ehr.", ""))
      : getWorkflowOriginalValue(
          WORKFLOW_DEFINITIONS.find((workflow) => workflow.id === workflowId)
        );

    setWorkflowDrafts((current) => {
      const nextDrafts = { ...current };

      if (value === originalValue) {
        delete nextDrafts[workflowId];
        return nextDrafts;
      }

      nextDrafts[workflowId] = value;
      return nextDrafts;
    });
  };

  const discardWorkflowChanges = () => {
    setWorkflowDrafts({});
  };

  const saveWorkflowChanges = async () => {
    if (!canWriteAdminSettings) {
      showReadOnlyToast();
      return;
    }

    if (!selectedUser) {
      toast({
        title: "No user selected",
        description: "Choose one team member before saving access changes.",
      });
      return;
    }

    const workflowOverrides = WORKFLOW_DEFINITIONS.reduce((acc, workflow) => {
      if (!workflowDrafts[workflow.id]) {
        return acc;
      }

      return {
        ...acc,
        ...getWorkflowPermissionUpdates(workflow, workflowDrafts[workflow.id]),
      };
    }, {});

    const advancedOverrides = EHR_ADVANCED_CONTROLS.reduce((acc, control) => {
      const draftValue = workflowDrafts[`ehr.${control.permissionKey}`];
      if (!draftValue) {
        return acc;
      }

      acc[control.permissionKey] = draftValue === "submit" ? "write" : draftValue;
      return acc;
    }, {});

    const overrides = {
      ...workflowOverrides,
      ...advancedOverrides,
    };

    if (Object.keys(overrides).length === 0) {
      setWorkflowDrafts({});
      return;
    }

    setSavingPermissionKey("workflow-overview");

    try {
      await manageRbacOverrides({
        userIds: [selectedUser.userId || selectedUser.id],
        overrides,
      });

      const timestamp = new Date().toISOString();
      const targetUserKey = getUserKey(selectedUser);
      setUsers((currentUsers) =>
        currentUsers.map((user) => {
          if (getUserKey(user) !== targetUserKey) {
            return user;
          }

          const customPermissions = {
            ...(user.customPermissions || {}),
            overrides: {
              ...(user.customPermissions?.overrides || {}),
              ...overrides,
            },
            lastUpdatedAt: timestamp,
          };

          return {
            ...user,
            customPermissions,
            effectivePermissions: computeEffectivePermissions(
              user.role,
              customPermissions,
              rolePermissionsByName[user.role] || null
            ),
          };
        })
      );

      setWorkflowDrafts({});
      toast({
        title: "Access changes saved",
        description: `${getDisplayName(selectedUser)} now has the updated workflow access.`,
      });
    } catch (error) {
      toast({
        title: "Failed to save access changes",
        description: error?.message || "The RBAC backend endpoint is not available yet.",
      });
    } finally {
      setSavingPermissionKey("");
    }
  };

  const openSelectedRoleDetails = () => {
    const selectedRole = roleInventory.find(
      (role) => normalizeRole(role.roleName) === normalizeRole(selectedUser?.role)
    );

    if (selectedRole) {
      openRoleEditor("view", selectedRole);
    }

    handleTabChange("roles");
  };

  const applyRoleAssignment = async () => {
    if (!canWriteAdminSettings) {
      showReadOnlyToast();
      return;
    }

    if (selectedUsers.length === 0) {
      toast({
        title: "No users selected",
        description: "Choose one or more users before assigning a role.",
      });
      return;
    }

    if (!selectedAssignmentRole) {
      toast({
        title: "Choose a role",
        description: "Pick a role before applying the assignment.",
      });
      return;
    }

    setAssigningRole(true);

    try {
      await assignRole({
        userIds: selectedUsers.map((user) => user.userId || user.id),
        roleName: selectedAssignmentRole,
        clinicName: loggedInClinicName || "",
      });

      setUsers((currentUsers) =>
        currentUsers.map((user) => {
          if (!selectedUserIds.includes(getUserKey(user))) {
            return user;
          }

          return {
            ...user,
            role: selectedAssignmentRole,
            effectivePermissions: computeEffectivePermissions(
              selectedAssignmentRole,
              user.customPermissions,
              rolePermissionsByName[selectedAssignmentRole] || null
            ),
          };
        })
      );

      toast({
        title: "Role assigned",
        description: `${selectedAssignmentRole} applied to ${selectedUsers.length} user(s).`,
      });
    } catch (error) {
      toast({
        title: "Failed to assign role",
        description: error?.message || "The assign-role endpoint is not available yet.",
      });
    } finally {
      setAssigningRole(false);
    }
  };

  const openRoleEditor = (mode, role = null) => {
    if (!canWriteAdminSettings && mode !== "view") {
      setEditorMode("view");
      setRoleEditorScrollRequest((current) => current + 1);
      if (role) {
        const nextRoleForm = createRoleDraftFromDoc(role);
        setRoleForm(nextRoleForm);
        setRoleEditorBaseline(cloneRoleEditorSnapshot(nextRoleForm));
      }
      showReadOnlyToast();
      return;
    }

    setEditorMode(mode);
    setRoleEditorScrollRequest((current) => current + 1);

    if (mode === "create") {
      const nextRoleForm = createRoleDraft("Staff", rolePermissionsByName);
      setRoleForm(nextRoleForm);
      setRoleEditorBaseline(cloneRoleEditorSnapshot(nextRoleForm));
      return;
    }

    if (role) {
      const nextRoleForm = createRoleDraftFromDoc(role);
      setRoleForm(nextRoleForm);
      setRoleEditorBaseline(cloneRoleEditorSnapshot(nextRoleForm));
    }
  };

  const cancelRoleEditor = () => {
    setEditorMode("");
    const nextRoleForm = createRoleDraft("Staff", rolePermissionsByName);
    setRoleForm(nextRoleForm);
    setRoleEditorBaseline(cloneRoleEditorSnapshot(nextRoleForm));
  };

  const discardRoleChanges = () => {
    if (!roleEditorBaseline) {
      cancelRoleEditor();
      return;
    }

    setRoleForm({
      ...roleEditorBaseline,
      permissions: {
        ...roleEditorBaseline.permissions,
      },
    });
    setRoleEditorBaseline(cloneRoleEditorSnapshot(roleEditorBaseline));
  };

  const handleRoleFieldChange = (field, value) => {
    setRoleForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleBaseRoleChange = (value) => {
    const nextBaseRole = normalizeRole(value) || "Staff";
    const sourceRole = roleInventory.find(
      (role) => normalizeRole(role.roleName) === normalizeRole(nextBaseRole)
    );
    const nextSkipNpiValidation = isClinicalNpiRole(nextBaseRole)
      ? false
      : nextBaseRole === "Staff" || Boolean(sourceRole?.skipNpiValidation);
    const nextRoleForm = {
      ...roleForm,
      baseRoleClonedFrom: nextBaseRole,
      showInRegistration: true,
      skipNpiValidation: nextSkipNpiValidation,
      permissions: {
        ...(rolePermissionsByName[nextBaseRole] ||
          getBasePermissionsForRole(nextBaseRole)),
      },
    };

    setRoleForm(nextRoleForm);
    setRoleEditorBaseline(cloneRoleEditorSnapshot(nextRoleForm));
  };

  const handleRolePermissionChange = (permissionKey, level) => {
    setRoleForm((current) => ({
      ...current,
      permissions: {
        ...current.permissions,
        [permissionKey]: level,
      },
    }));
  };

  const getRoleWorkflowValue = (workflow) =>
    getWorkflowAccessState(workflow, roleForm.permissions || {}).value;

  const handleRoleWorkflowChange = (workflow, level) => {
    if (isRoleReadOnly) {
      return;
    }

    setRoleForm((current) => ({
      ...current,
      permissions: {
        ...current.permissions,
        ...getWorkflowPermissionUpdates(workflow, level),
      },
    }));
  };

  const toggleRoleWorkflowAdvanced = (workflowId) => {
    setExpandedRoleWorkflows((current) => ({
      ...current,
      [workflowId]: !current[workflowId],
    }));
  };

  const getWorkflowPermissionKeys = (workflowId) =>
    getWorkflowPermissionKeysFromCatalog(workflowId);

  const saveRoleDefinition = async () => {
    if (!canWriteAdminSettings) {
      showReadOnlyToast();
      return;
    }

    const trimmedRoleName = roleForm.roleName.trim();

    if (!trimmedRoleName) {
      toast({
        title: "Role name required",
        description: "Add a role name before saving.",
      });
      return;
    }

    setSavingRole(true);

    const payload = {
      clinicName: loggedInClinicName || "",
      roleName: trimmedRoleName,
      description: roleForm.description.trim(),
      baseRoleClonedFrom: roleForm.baseRoleClonedFrom || undefined,
      showInRegistration: roleForm.showInRegistration,
      skipNpiValidation: roleForm.skipNpiValidation,
      permissions: buildPermissionsMap(roleForm.permissions),
    };

    try {
      const fallbackRole = {
        ...payload,
        id: roleForm.id || trimmedRoleName.toLowerCase().replace(/\s+/g, "_"),
        type: roleForm.type || "custom",
        isActive: true,
      };

      const response =
        editorMode === "create"
          ? await createRole(payload)
          : await updateRole(roleForm.id, payload);

      const savedRole = createRoleDraftFromDoc(getRolePayload(response, fallbackRole));

      setRoles((currentRoles) => {
        const nextRoles = currentRoles.filter(
          (role) =>
            normalizeRole(role.roleName) !== normalizeRole(savedRole.roleName)
        );

        return [
          ...nextRoles,
          {
            ...savedRole,
            isActive: true,
          },
        ];
      });

      setUsers((currentUsers) =>
        currentUsers.map((user) => {
          if (normalizeRole(user.role) !== normalizeRole(savedRole.roleName)) {
            return user;
          }

          return {
            ...user,
            effectivePermissions: computeEffectivePermissions(
              user.role,
              user.customPermissions,
              savedRole.permissions
            ),
          };
        })
      );

      toast({
        title: editorMode === "create" ? "Role created" : "Role updated",
        description: `${trimmedRoleName} is ready to use for ${loggedInClinicName || "your clinic"}.`,
      });

      cancelRoleEditor();
    } catch (error) {
      toast({
        title: "Failed to save role",
        description: error?.message || "The role management endpoint is not available yet.",
      });
    } finally {
      setSavingRole(false);
    }
  };

  const removeRoleDefinition = async () => {
    if (!canWriteAdminSettings) {
      showReadOnlyToast();
      return;
    }

    if (!rolePendingDelete) {
      return;
    }

    const role = rolePendingDelete;
    const requiresReplacement = (role.userCount || 0) > 0;

    if (requiresReplacement && !deleteReplacementRole) {
      toast({
        title: "Choose a replacement role",
        description: `Assign ${role.userCount} user(s) on ${role.roleName} to another role before deleting it.`,
      });
      return;
    }

    setDeletingRoleId(role.id);

    try {
      await deleteRole(role.id, {
        clinicName: loggedInClinicName || "",
        replacementRoleName: requiresReplacement ? deleteReplacementRole : undefined,
      });

      setRoles((currentRoles) =>
        currentRoles.filter(
          (currentRole) =>
            normalizeRole(currentRole.roleName) !==
            normalizeRole(role.roleName)
        )
      );

      if (requiresReplacement) {
        setUsers((currentUsers) =>
          currentUsers.map((user) => {
            if (normalizeRole(user.role) !== normalizeRole(role.roleName)) {
              return user;
            }

            return {
              ...user,
              role: deleteReplacementRole,
              effectivePermissions: computeEffectivePermissions(
                deleteReplacementRole,
                user.customPermissions,
                rolePermissionsByName[deleteReplacementRole] || null
              ),
            };
          })
        );
      }

      if (normalizeRole(roleForm.roleName) === normalizeRole(role.roleName)) {
        cancelRoleEditor();
      }

      toast({
        title: "Role deleted",
        description: requiresReplacement
          ? `${role.roleName} has been removed and its users were reassigned to ${deleteReplacementRole}.`
          : `${role.roleName} has been removed from active custom roles.`,
      });
    } catch (error) {
      toast({
        title: "Failed to delete role",
        description:
          error?.message || "The delete-role endpoint is not available yet.",
      });
    } finally {
      setDeletingRoleId("");
      setRolePendingDelete(null);
      setDeleteReplacementRole("");
    }
  };

  const handleApprovePendingUser = async (user) => {
    if (!canWriteAdminSettings) {
      showReadOnlyToast();
      return;
    }

    setApprovingUserId(user.userId);

    try {
      const response = await approveUser(user.userId);
      const approvedUser = response?.user || {
        ...user,
        approvalStatus: "approved",
        prodAccessGranted: true,
      };

      setPendingApprovals((current) =>
        current.filter((entry) => entry.userId !== user.userId)
      );
      setUsers((current) =>
        current.map((entry) =>
          entry.userId === user.userId
            ? {
                ...entry,
                ...approvedUser,
              }
            : entry
        )
      );

      toast({
        title: "User approved",
        description: `${getDisplayName(user)} can now access the app.`,
      });
      await refreshUsersSnapshot();
    } catch (error) {
      toast({
        title: "Failed to approve user",
        description: error?.message || "Please try again.",
      });
    } finally {
      setApprovingUserId("");
    }
  };

  const handleRejectPendingUser = async (user) => {
    if (!canWriteAdminSettings) {
      showReadOnlyToast();
      return;
    }

    setRejectingUserId(user.userId);

    try {
      const response = await rejectUser(user.userId);
      const rejectedUser = response?.user || {
        ...user,
        approvalStatus: "rejected",
        prodAccessGranted: false,
      };

      setPendingApprovals((current) =>
        current.filter((entry) => entry.userId !== user.userId)
      );
      setUsers((current) =>
        current.map((entry) =>
          entry.userId === user.userId
            ? {
                ...entry,
                ...rejectedUser,
              }
            : entry
        )
      );

      toast({
        title: "User rejected",
        description: `${getDisplayName(user)} no longer has a pending approval request.`,
      });
      setApprovalPendingReject(null);
      await refreshUsersSnapshot();
    } catch (error) {
      toast({
        title: "Failed to reject user",
        description: error?.message || "Please try again.",
      });
    } finally {
      setRejectingUserId("");
    }
  };

  const refreshPendingApprovals = async () => {
    setRefreshingApprovals(true);
    setApprovalsError("");

    try {
      const approvals = await fetchPendingApprovals();
      setPendingApprovals(Array.isArray(approvals) ? approvals : []);
      toast({
        title: "Pending approvals refreshed",
        description: "Latest approval requests are now shown.",
      });
    } catch (error) {
      const message =
        error?.message || "Pending approvals could not be refreshed right now.";
      setApprovalsError(message);
      toast({
        title: "Refresh failed",
        description: message,
      });
    } finally {
      setRefreshingApprovals(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!canWriteAdminSettings) {
      showReadOnlyToast();
      return;
    }

    const normalizedEmail = invitationForm.email.trim().toLowerCase();
    const roleName = invitationForm.roleName;

    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      toast({
        title: "Valid email required",
        description: "Add the teammate email before sending the invitation.",
      });
      return;
    }

    if (!roleName) {
      toast({
        title: "Role required",
        description: "Choose a role for the invited teammate.",
      });
      return;
    }

    const selectedRole = roleInventory.find(
      (role) => normalizeRole(role.roleName) === normalizeRole(roleName)
    );

    setSendingInvitation(true);

    if (!subscription.canAddSeat()) {
      setShowMaxSeatModal(true);
      setSendingInvitation(false);
      return;
    }

    try {
      const response = await sendInvitation({
        email: normalizedEmail,
        roleName,
        skipNpiValidation: Boolean(selectedRole?.skipNpiValidation),
      });

      if (response?.invitation) {
        setInvitations((current) => [response.invitation, ...current]);
      }

      setInvitationForm({
        email: "",
        roleName: "",
      });

      toast({
        title: "Invitation sent",
        description: `An invitation was sent to ${normalizedEmail}.`,
      });
    } catch (error) {
      toast({
        title: "Failed to send invitation",
        description: error?.message || "Please check the SMTP settings and try again.",
      });
    } finally {
      setSendingInvitation(false);
    }
  };

  const handleRevokeInvitation = async (invitation) => {
    if (!canWriteAdminSettings) {
      showReadOnlyToast();
      return;
    }

    setRevokingInvitationId(invitation.id);

    try {
      await revokeInvitation(invitation.id);
      setInvitations((current) => current.filter((entry) => entry.id !== invitation.id));
      toast({
        title: "Invitation revoked",
        description: `${invitation.invitedEmail} can no longer use that invitation.`,
      });
    } catch (error) {
      toast({
        title: "Failed to revoke invitation",
        description: error?.message || "Please try again.",
      });
    } finally {
      setRevokingInvitationId("");
    }
  };

  const isRoleReadOnly =
    !canWriteAdminSettings ||
    editorMode === "view" ||
    roleForm.type === "system" ||
    !editorMode;

  const originalRoleForm = roleEditorBaseline;

  const isRoleFieldModified = (field) => {
    if (!originalRoleForm) {
      return false;
    }

    if (field === "roleName" || field === "description") {
      return (
        (roleForm[field] || "").trim() !==
        (originalRoleForm[field] || "").trim()
      );
    }

    if (field === "baseRoleClonedFrom") {
      return (
        normalizeRole(roleForm.baseRoleClonedFrom) !==
        normalizeRole(originalRoleForm.baseRoleClonedFrom)
      );
    }

    return Boolean(roleForm[field]) !== Boolean(originalRoleForm[field]);
  };

  const getOriginalRoleWorkflowValue = (workflow) =>
    originalRoleForm
      ? getWorkflowAccessState(workflow, originalRoleForm.permissions || {}).value
      : "";

  const getOriginalRolePermissionLevel = (permissionKey) =>
    originalRoleForm
      ? getPermissionLevel(originalRoleForm.permissions, permissionKey)
      : "none";

  const isRolePermissionModified = (permissionKey) =>
    getPermissionLevel(roleForm.permissions, permissionKey) !==
    getOriginalRolePermissionLevel(permissionKey);

  const isRoleWorkflowModified = (workflow) => {
    const permissionKeys = getWorkflowPermissionKeys(workflow.id);

    return (
      getRoleWorkflowValue(workflow) !== getOriginalRoleWorkflowValue(workflow) ||
      permissionKeys.some((permissionKey) =>
        isRolePermissionModified(permissionKey)
      )
    );
  };

  const hasUnsavedRoleChanges = useMemo(() => {
    if (!editorMode || isRoleReadOnly) {
      return false;
    }

    if (!originalRoleForm) {
      return true;
    }

    return (
      (roleForm.roleName || "").trim() !==
        (originalRoleForm.roleName || "").trim() ||
      (roleForm.description || "").trim() !==
        (originalRoleForm.description || "").trim() ||
      normalizeRole(roleForm.baseRoleClonedFrom) !==
        normalizeRole(originalRoleForm.baseRoleClonedFrom) ||
      Boolean(roleForm.showInRegistration) !==
        Boolean(originalRoleForm.showInRegistration) ||
      Boolean(roleForm.skipNpiValidation) !==
        Boolean(originalRoleForm.skipNpiValidation) ||
      JSON.stringify(buildPermissionsMap(roleForm.permissions)) !==
        JSON.stringify(buildPermissionsMap(originalRoleForm.permissions))
    );
  }, [editorMode, isRoleReadOnly, originalRoleForm, roleForm]);

  return (
    <div className="space-y-3 px-2 pb-4">
      <AlertDialog
        open={Boolean(rolePendingDelete)}
        onOpenChange={(open) => {
          if (!open && !deletingRoleId) {
            setRolePendingDelete(null);
            setDeleteReplacementRole("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role?</AlertDialogTitle>
            <AlertDialogDescription>
              {rolePendingDelete
                ? `${rolePendingDelete.roleName} will be removed from active custom roles.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {rolePendingDelete ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {rolePendingDelete.userCount > 0
                  ? `${rolePendingDelete.userCount} user(s) are currently assigned to ${rolePendingDelete.roleName}. Choose a replacement role before deleting it.`
                  : "No users are currently assigned to this role, so it can be deleted immediately."}
              </div>
              {rolePendingDelete.userCount > 0 ? (
                <div className="space-y-2">
                  <label
                    htmlFor="replacement-role"
                    className="text-sm font-medium text-slate-900"
                  >
                    Reassign users to
                  </label>
                  <select
                    id="replacement-role"
                    value={deleteReplacementRole}
                    onChange={(event) => setDeleteReplacementRole(event.target.value)}
                    disabled={Boolean(deletingRoleId)}
                    className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose a replacement role</option>
                    {deleteReplacementRoleOptions.map((roleName) => (
                      <option key={roleName} value={roleName}>
                        {roleName}
                      </option>
                    ))}
                  </select>
                  {deleteReplacementRoleOptions.length === 0 ? (
                    <p className="text-sm text-red-600">
                      Create another active role first so these users have somewhere to move.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingRoleId)}>
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              onClick={removeRoleDefinition}
              disabled={
                !canWriteAdminSettings ||
                Boolean(deletingRoleId) ||
                ((rolePendingDelete?.userCount || 0) > 0 && !deleteReplacementRole)
              }
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            >
              {deletingRoleId ? "Deleting..." : "Delete role"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(approvalPendingReject)}
        onOpenChange={(open) => {
          if (!open && !rejectingUserId) {
            setApprovalPendingReject(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject pending user?</AlertDialogTitle>
            <AlertDialogDescription>
              {approvalPendingReject
                ? `${getDisplayName(approvalPendingReject)} will lose this pending request and stay blocked from app access until re-approved later.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(rejectingUserId)}>
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              onClick={() =>
                approvalPendingReject
                  ? handleRejectPendingUser(approvalPendingReject)
                  : null
              }
              disabled={!canWriteAdminSettings || Boolean(rejectingUserId)}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            >
              {rejectingUserId ? "Rejecting..." : "Reject User"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PageNavigation
        title="Admin Settings"
        subtitle={
          loggedInClinicName
            ? `Manage clinical team members, their access, custom roles, and security permissions across ${loggedInClinicName}.`
            : "Manage your personal user record and clinical access."
        }
      />

      {!canWriteAdminSettings ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Admin Settings is in read-only mode for this user. Existing access can be reviewed, but it cannot be changed.
        </div>
      ) : null}

      <div className="border-b border-gray-200 mb-6" data-tour="admin-tabs">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {TAB_OPTIONS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              data-tour={`admin-tab-${tab.id}`}
              onClick={() => handleTabChange(tab.id)}
              className={`whitespace-nowrap pb-4 px-2 border-b-2 font-medium text-[15px] transition-colors ${
                activeTab === tab.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "permissions" || activeTab === "roles" ? (
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
        <Card className="border border-gray-200 shadow-sm" data-tour="admin-users-list">
          <CardHeader className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4 text-blue-600" />
                {activeTab === "permissions" ? "Select User" : "Select Users"}
              </CardTitle>
              <Badge variant="outline">{selectedUsers.length} selected</Badge>
            </div>
            {activeTab === "permissions" ? (
              <select
                value={selectedUser ? getUserKey(selectedUser) : ""}
                onChange={(event) => selectPermissionUser(event.target.value)}
                className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a team member</option>
                {users
                  .filter((user) => !isCurrentLoggedInUser(user, loggedInEmail))
                  .map((user) => (
                    <option key={getUserKey(user)} value={getUserKey(user)}>
                      {getDisplayName(user)} - {user.role}
                    </option>
                  ))}
              </select>
            ) : null}
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search team members"
                className="pl-9"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All roles</option>
              {availableRoleOptions.map((roleName) => (
                <option key={roleName} value={roleName}>
                  {roleName}
                </option>
              ))}
            </select>
            {activeTab === "roles" ? (
            <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
                <UserCog className="h-4 w-4" />
                Assign role to selected users
              </div>
              <div className="mt-3 space-y-3">
                <select
                  value={selectedAssignmentRole}
                  onChange={(event) => setSelectedAssignmentRole(event.target.value)}
                  disabled={!canWriteAdminSettings}
                  className="h-10 w-full rounded-md border border-blue-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose a role</option>
                  {availableRoleOptions.map((roleName) => (
                    <option key={roleName} value={roleName}>
                      {roleName}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  onClick={applyRoleAssignment}
                  disabled={
                    !canWriteAdminSettings ||
                    assigningRole ||
                    selectedUsers.length === 0
                  }
                  className="w-full bg-blue-600 text-white hover:bg-blue-700"
                >
                  {assigningRole ? "Applying role..." : "Apply role"}
                </Button>
              </div>
            </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => handleTabChange("invites")}
                disabled={!canWriteAdminSettings}
                className="w-full justify-center"
              >
                <Plus className="mr-2 h-4 w-4" />
                Invite New User
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-sm text-gray-500">Loading users...</div>
            ) : loadError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {loadError}
              </div>
            ) : users.length > 0 && filteredUsers.length === 0 && !search.trim() ? (
              <div className="text-sm text-gray-500">
                No other users are available to manage in Admin Settings.
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-sm text-gray-500">No users matched the current search.</div>
            ) : (
              <div className="max-h-[720px] space-y-4 overflow-y-auto pr-1">
                {Object.values(groupedUsers)
                  .sort((a, b) => a.role.localeCompare(b.role))
                  .map((group) => {
                    const roleUserIds = group.users.map((user) => getUserKey(user));
                    const allRoleUsersSelected =
                      roleUserIds.length > 0 &&
                      roleUserIds.every((id) => selectedUserIds.includes(id));

                    return (
                      <div key={group.role} className="rounded-xl border border-gray-200">
                        <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3">
                          <div className="font-medium text-gray-900">{group.role}</div>
                          {activeTab === "roles" ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => toggleRoleUsers(roleUserIds)}
                            className="h-8 px-3 text-xs"
                          >
                            {allRoleUsersSelected ? "Unselect all" : "Select all"}
                          </Button>
                          ) : null}
                        </div>
                        <div className="space-y-3 p-4">
                          {group.users.map((user) => {
                            const userId = getUserKey(user);
                            return (
                              <button
                                key={userId}
                                type="button"
                                onClick={() =>
                                  activeTab === "permissions"
                                    ? selectPermissionUser(userId)
                                    : toggleUser(userId)
                                }
                                className={`flex w-full cursor-pointer items-center gap-3 rounded-xl border p-3 text-left transition-all duration-150 ${
                                  selectedUserIds.includes(userId)
                                    ? "border-blue-400 bg-blue-50 shadow-sm ring-1 ring-blue-200"
                                    : "border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/30"
                                }`}
                              >
                                <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border-2 transition-colors ${
                                  selectedUserIds.includes(userId)
                                    ? "border-blue-600 bg-blue-600"
                                    : "border-gray-400 bg-white"
                                }`}
                                >
                                  {selectedUserIds.includes(userId) && (
                                    <svg viewBox="0 0 10 8" className="h-2.5 w-2.5">
                                      <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-inner shrink-0 items-center justify-center rounded-full h-10 w-10 text-[13px] tracking-wider font-semibold">
                                  {getInitials(getDisplayName(user))}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-sm font-semibold text-slate-800">
                                    {getDisplayName(user)}
                                  </div>
                                  <div className="truncate text-[11px] text-slate-500 font-medium">
                                    {user.email || user.id}
                                  </div>
                                  <div className="mt-1 text-[11px] font-medium text-slate-500">
                                    {user.role}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                })}
              </div>
            )}
            {activeTab === "permissions" ? (
              <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50/60 p-3 text-sm text-blue-950">
                <div className="font-semibold">About Access Levels</div>
                <div className="mt-3 space-y-2.5">
                  {ACCESS_LEGEND_ITEMS.map((item) => {
                    const Icon = item.icon;

                    return (
                      <div key={item.label} className="flex gap-2.5">
                        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white text-blue-600 shadow-sm">
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-blue-950">
                            {item.label}
                          </div>
                          <div className="text-xs leading-5 text-blue-900">
                            {item.description}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <div className="space-y-4" data-tour={activeTab === "permissions" ? "admin-permissions-panel" : undefined}>
          {rolesError ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              {rolesError}
            </div>
          ) : null}

          {activeTab === "permissions" ? (
            <>
              {selectedUsers.length === 0 ? (
                <Card className="border border-dashed border-gray-300 bg-white shadow-sm">
                  <CardContent className="flex min-h-[320px] flex-col items-center justify-center text-center">
                    <ShieldCheck className="mb-4 h-10 w-10 text-blue-600" />
                    <h2 className="text-lg font-semibold text-gray-900">
                      Choose a user to manage
                    </h2>
                    <p className="mt-2 max-w-xl text-sm text-gray-600">
                      Select a team member from the left to review their role template
                      and adjust workflow access without exposing every technical
                      permission by default.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className={`space-y-4 ${hasUnsavedWorkflowChanges ? "pb-36" : ""}`}>
                  <Card className="border border-gray-200 shadow-sm">
                    <CardContent className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Permission Overview
                        </div>
                        <h2 className="mt-1 text-lg font-semibold text-gray-900">
                          Using Role Template: {selectedUser?.role || "Unassigned"}
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                          Custom changes here apply only to {getDisplayName(selectedUser)}.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={openSelectedRoleDetails}
                        >
                          View Role Details
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleTabChange("roles")}
                        >
                          View Role Templates
                        </Button>
                        <Button
                          type="button"
                          onClick={saveWorkflowChanges}
                          disabled={
                            !canWriteAdminSettings ||
                            !hasUnsavedWorkflowChanges ||
                            savingPermissionKey === "workflow-overview"
                          }
                          className="bg-blue-600 text-white hover:bg-blue-700"
                        >
                          {savingPermissionKey === "workflow-overview"
                            ? "Saving..."
                            : "Save Changes"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
                    {WORKFLOW_DEFINITIONS.map((workflow) => {
                      const Icon = workflow.icon;
                      const value = getWorkflowValue(workflow);
                      const isCustomWorkflowValue =
                        value === CUSTOM_WORKFLOW_ACCESS_VALUE;
                      const isExpanded = Boolean(expandedWorkflows[workflow.id]);
                      const roleDefaultValue = getWorkflowAccessState(
                        workflow,
                        selectedUserRolePermissions
                      ).value;
                      const roleDefaultLabel = getWorkflowAccessLabel(
                        workflow,
                        roleDefaultValue
                      );
                      const currentAccessLabel = getWorkflowAccessLabel(
                        workflow,
                        value
                      );
                      const showCustomAccessNote =
                        isCustomWorkflowValue || value !== roleDefaultValue;
                      const isModified =
                        Object.prototype.hasOwnProperty.call(workflowDrafts, workflow.id) ||
                        (workflow.id === "postCallDocumentation" &&
                          EHR_ADVANCED_CONTROLS.some((control) =>
                            Object.prototype.hasOwnProperty.call(
                              workflowDrafts,
                              `ehr.${control.permissionKey}`
                            )
                          ));

                      return (
                        <Card
                          key={workflow.id}
                          className={`border shadow-sm transition-colors ${
                            isModified
                              ? "border-amber-300 bg-amber-50/50 ring-1 ring-amber-100"
                              : "border-gray-200 bg-white"
                          }`}
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-start gap-3">
                              <div
                                className={`rounded-lg p-2 ${
                                  isModified
                                    ? "bg-amber-100 text-amber-700"
                                    : "bg-blue-50 text-blue-600"
                                }`}
                              >
                                <Icon className="h-5 w-5" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <CardTitle className="text-base">
                                    {workflow.title}
                                  </CardTitle>
                                  {isModified ? (
                                    <Badge
                                      variant="outline"
                                      className="border-amber-200 bg-amber-100 text-[11px] font-semibold text-amber-800"
                                    >
                                      Modified
                                    </Badge>
                                  ) : null}
                                </div>
                                <p className="mt-1 text-sm text-gray-500">
                                  {workflow.description}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => toggleWorkflow(workflow.id)}
                                className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
                                aria-label={`Toggle ${workflow.title}`}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <InlineAccessPicker
                              id={`workflow.${workflow.id}`}
                              value={value}
                              options={workflow.options}
                              disabled={!canWriteAdminSettings}
                              openPickerId={openAccessPickerId}
                              setOpenPickerId={setOpenAccessPickerId}
                              onChange={(nextValue) =>
                                handleWorkflowDraftChange(
                                  workflow.id,
                                  nextValue
                                )
                              }
                            />
                            {isCustomWorkflowValue ? (
                              <p className="text-xs font-medium text-amber-700">
                                {CUSTOM_WORKFLOW_ACCESS_HELPER_TEXT}
                              </p>
                            ) : null}

                            {isExpanded ? (
                              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                                <div className="mb-3">
                                  <AccessDefaultSummary
                                    defaultLevel={roleDefaultLabel}
                                    currentLevel={currentAccessLabel}
                                    isCustom={showCustomAccessNote}
                                  />
                                </div>
                                {workflow.id === "postCallDocumentation" ? (
                                  <div className="space-y-3">
                                    <div>
                                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Includes
                                      </div>
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {POST_CALL_INCLUDES.map((item) => (
                                          <Badge
                                            key={item}
                                            variant="outline"
                                            className="bg-white text-gray-700"
                                          >
                                            {item}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-white">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setAdvancedEhrOpen((current) => !current)
                                        }
                                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm font-medium text-gray-900"
                                      >
                                        Advanced Controls for EHR submission
                                        {advancedEhrOpen ? (
                                          <ChevronDown className="h-4 w-4 text-gray-500" />
                                        ) : (
                                          <ChevronRight className="h-4 w-4 text-gray-500" />
                                        )}
                                      </button>
                                      {advancedEhrOpen ? (
                                        <div className="space-y-3 border-t border-gray-100 p-3">
                                          {EHR_ADVANCED_CONTROLS.map((control) => (
                                            <div
                                              key={control.id}
                                              className="grid grid-cols-1 gap-2 md:grid-cols-[170px_minmax(0,1fr)] md:items-center"
                                            >
                                              <div className="text-sm font-medium text-gray-700">
                                                {control.label}
                                              </div>
                                              <InlineAccessPicker
                                                id={`ehr.${control.permissionKey}`}
                                                value={getAdvancedEhrValue(
                                                  control.permissionKey
                                                )}
                                                options={EHR_CONTROL_OPTIONS}
                                                disabled={!canWriteAdminSettings}
                                                openPickerId={openAccessPickerId}
                                                setOpenPickerId={setOpenAccessPickerId}
                                                onChange={(nextValue) =>
                                                  handleWorkflowDraftChange(
                                                    `ehr.${control.permissionKey}`,
                                                    nextValue
                                                  )
                                                }
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {hasUnsavedWorkflowChanges ? (
                    <div className="sticky bottom-5 z-30 w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-[0_18px_45px_rgba(15,23,42,0.16)] ring-1 ring-white/80">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 rounded-full bg-amber-50 p-2 text-amber-600 ring-1 ring-amber-100">
                            <AlertTriangle className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              You have unsaved changes
                            </div>
                            <div className="mt-0.5 text-xs text-gray-600">
                              Save these workflow updates or discard them before switching context.
                            </div>
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={discardWorkflowChanges}
                            disabled={savingPermissionKey === "workflow-overview"}
                            className="border-gray-300 bg-white"
                          >
                            Discard Changes
                          </Button>
                          <Button
                            type="button"
                            onClick={saveWorkflowChanges}
                            disabled={
                              !canWriteAdminSettings ||
                              savingPermissionKey === "workflow-overview"
                            }
                            className="bg-blue-600 text-white hover:bg-blue-700"
                          >
                            {savingPermissionKey === "workflow-overview"
                              ? "Saving..."
                              : "Save Changes"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card className="border border-gray-200 shadow-sm">
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="rounded-full bg-blue-50 p-3 text-blue-600">
                      <BriefcaseBusiness className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500">
                        Total Roles
                      </div>
                      <div className="text-2xl font-semibold text-gray-900">
                        {roleInventory.length}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-gray-200 shadow-sm">
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="rounded-full bg-emerald-50 p-3 text-emerald-600">
                      <Plus className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500">
                        Custom Roles
                      </div>
                      <div className="text-2xl font-semibold text-gray-900">
                        {customRoleCount}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-gray-200 shadow-sm">
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="rounded-full bg-amber-50 p-3 text-amber-600">
                      <Users className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500">
                        Users On Custom Roles
                      </div>
                      <div className="text-2xl font-semibold text-gray-900">
                        {usersOnCustomRoles}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="border border-gray-200 shadow-sm" data-tour="admin-roles-grid">
                <CardHeader className="flex flex-row items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">Clinic Roles</CardTitle>
                    <p className="mt-1 text-sm text-gray-500">
                      System roles are read-only. Custom roles are scoped to the current clinic.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={() => openRoleEditor("create")}
                    disabled={!canWriteAdminSettings}
                    className="bg-blue-600 text-white hover:bg-blue-700"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create New Role
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {roleInventory.map((role) => {
                      const isOpenRole =
                        editorMode &&
                        normalizeRole(role.roleName) ===
                          normalizeRole(roleForm.roleName);

                      return (
                      <div
                        key={role.id || role.roleName}
                        className={`relative flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition-all hover:shadow-md ${
                          isOpenRole
                            ? "border-blue-300 bg-blue-50/30 ring-2 ring-blue-100"
                            : "border-gray-200"
                        }`}
                      >
                        {isOpenRole ? (
                          <div className="absolute left-0 top-0 h-full w-1.5 bg-blue-500" />
                        ) : null}
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 pl-1">
                              <h3 className="text-lg font-semibold text-slate-900">{role.roleName}</h3>
                              <Badge variant="secondary" className="mt-2 tracking-wide text-[10px] uppercase bg-slate-100 text-slate-600 border-none">
                                {role.type === "system" ? "System Role" : "Custom Role"}
                              </Badge>
                            </div>
                            <div className="flex h-11 w-11 flex-shrink-0 flex-col items-center justify-center rounded-full bg-blue-50 text-blue-600 shadow-inner">
                              <Users className="h-5 w-5" />
                            </div>
                          </div>
                          
                          <div className="mt-6 space-y-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500 font-medium tracking-tight">Active Users</span>
                              <span className="font-semibold text-slate-900">{role.userCount || 0}</span>
                            </div>
                            {role.type !== "system" && (
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-500 font-medium tracking-tight">Base Role</span>
                                <span className="font-medium text-slate-700">{role.baseRoleClonedFrom || "—"}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500 font-medium tracking-tight">Registration</span>
                              <span className="font-medium text-slate-700">{role.showInRegistration ? "Visible" : "Hidden"}</span>
                            </div>
                            {role.description ? (
                              <p className="line-clamp-2 rounded-lg bg-slate-50 px-3 py-2 text-sm leading-5 text-slate-600">
                                {role.description}
                              </p>
                            ) : null}
                          </div>
                        </div>

                        <div className="mt-auto flex items-center gap-2 border-t border-slate-100 bg-slate-50/50 p-4 rounded-b-2xl">
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1 bg-white border-slate-200 text-sm font-semibold hover:bg-slate-50 hover:text-blue-700 transition"
                            onClick={() =>
                              openRoleEditor(
                                role.type === "system" || !canWriteAdminSettings
                                  ? "view"
                                  : "edit",
                                role
                              )
                            }
                          >
                            {role.type === "system" || !canWriteAdminSettings ? "View Details" : "Edit Role"}
                          </Button>
                          {role.type !== "system" ? (
                            <Button
                              type="button"
                              className="px-3 shadow-none bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition"
                              onClick={() => {
                                setRolePendingDelete(role);
                                setDeleteReplacementRole("");
                              }}
                              disabled={!canWriteAdminSettings || deletingRoleId === role.id}
                              title="Delete Role"
                            >
                              <Trash2 className="h-[18px] w-[18px]" />
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card
                ref={roleEditorRef}
                className="scroll-mt-6 border border-gray-200 shadow-sm"
                data-tour="admin-role-editor"
              >
                <CardHeader className="flex flex-row items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-base">
                        {editorMode === "create"
                          ? "Create New Role"
                          : editorMode === "edit"
                          ? "Edit Role"
                          : editorMode === "view"
                          ? "Role Details"
                          : "Role Editor"}
                      </CardTitle>
                      {hasUnsavedRoleChanges ? (
                        <Badge
                          variant="outline"
                          className="border-amber-200 bg-amber-100 text-[11px] font-semibold text-amber-800"
                        >
                          Unsaved Changes
                        </Badge>
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      {editorMode
                        ? "Define clinic-scoped role defaults, then refine permissions below."
                        : "Pick a role to edit or create a new role to start managing templates."}
                    </p>
                  </div>
                  {editorMode ? (
                    <Button type="button" variant="outline" onClick={cancelRoleEditor}>
                      Close
                    </Button>
                  ) : null}
                </CardHeader>
                <CardContent>
                  {!editorMode ? (
                    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
                      Select a role from the table or create a new one to edit permission defaults.
                    </div>
                  ) : (
                    <div className={`space-y-4 ${!isRoleReadOnly && hasUnsavedRoleChanges ? "pb-36" : ""}`}>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">
                            Role Name
                          </label>
                          <Input
                            value={roleForm.roleName}
                            onChange={(event) =>
                              handleRoleFieldChange("roleName", event.target.value)
                            }
                            disabled={isRoleReadOnly || editorMode === "edit"}
                            placeholder="Billing Specialist"
                            className={
                              isRoleFieldModified("roleName")
                                ? "border-amber-300 bg-amber-50/50 focus:ring-amber-500"
                                : ""
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">
                            Clone From
                          </label>
                          <select
                            value={roleForm.baseRoleClonedFrom || ""}
                            onChange={(event) => handleBaseRoleChange(event.target.value)}
                            disabled={isRoleReadOnly || editorMode === "edit"}
                            className={`h-10 w-full rounded-md border px-3 text-sm focus:outline-none focus:ring-2 ${
                              isRoleFieldModified("baseRoleClonedFrom")
                                ? "border-amber-300 bg-amber-50/50 focus:ring-amber-500"
                                : "border-gray-300 bg-white focus:ring-blue-500"
                            }`}
                          >
                            {availableRoleOptions.map((roleName) => (
                              <option key={roleName} value={roleName}>
                                {roleName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Description
                        </label>
                        <textarea
                          value={roleForm.description}
                          onChange={(event) =>
                            handleRoleFieldChange("description", event.target.value)
                          }
                          disabled={isRoleReadOnly}
                          className={`min-h-[96px] w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                            isRoleFieldModified("description")
                              ? "border-amber-300 bg-amber-50/50 focus:ring-amber-500"
                              : "border-gray-300 bg-white focus:ring-blue-500"
                          }`}
                          placeholder="Handles billing workflows and financial reporting."
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <label
                          className={`flex items-center justify-between gap-3 rounded-xl border p-4 ${
                            isRoleFieldModified("showInRegistration")
                              ? "border-amber-200 bg-amber-50"
                              : "border-gray-200"
                          }`}
                        >
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              Show In Registration
                            </div>
                            <div className="text-xs text-gray-500">
                              Allow self-registration with this role.
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={roleForm.showInRegistration}
                            onChange={(event) =>
                              handleRoleFieldChange(
                                "showInRegistration",
                                event.target.checked
                              )
                            }
                            disabled={isRoleReadOnly}
                            className="h-4 w-4"
                          />
                        </label>

                        <label
                          className={`flex items-center justify-between gap-3 rounded-xl border p-4 ${
                            isRoleFieldModified("skipNpiValidation")
                              ? "border-amber-200 bg-amber-50"
                              : "border-gray-200"
                          }`}
                        >
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              Skip NPI Validation
                            </div>
                            <div className="text-xs text-gray-500">
                              Useful for non-clinical custom roles.
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={roleForm.skipNpiValidation}
                            onChange={(event) =>
                              handleRoleFieldChange(
                                "skipNpiValidation",
                                event.target.checked
                              )
                            }
                            disabled={isRoleReadOnly}
                            className="h-4 w-4"
                          />
                        </label>
                      </div>

                      <div className="space-y-3">
                        <div className="flex flex-col gap-2 rounded-xl border border-blue-100 bg-blue-50/60 p-4 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="text-sm font-semibold text-blue-950">
                              Workflow Defaults
                            </div>
                            <div className="mt-1 text-xs leading-5 text-blue-900">
                              These cards update the existing RBAC permission keys for this role template.
                            </div>
                          </div>
                          {hasUnsavedRoleChanges ? (
                            <Badge
                              variant="outline"
                              className="w-fit border-amber-200 bg-amber-100 text-[11px] font-semibold text-amber-800"
                            >
                              Unsaved changes
                            </Badge>
                          ) : null}
                        </div>

                        <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
                          {WORKFLOW_DEFINITIONS.map((workflow) => {
                            const Icon = workflow.icon;
                            const workflowValue = getRoleWorkflowValue(workflow);
                            const workflowLabel = getWorkflowAccessLabel(
                              workflow,
                              workflowValue
                            );
                            const originalWorkflowValue =
                              getOriginalRoleWorkflowValue(workflow);
                            const originalWorkflowLabel = getWorkflowAccessLabel(
                              workflow,
                              originalWorkflowValue
                            );
                            const isCustomWorkflowValue =
                              workflowValue === CUSTOM_WORKFLOW_ACCESS_VALUE;
                            const isAdvancedOpen = Boolean(
                              expandedRoleWorkflows[workflow.id]
                            );
                            const permissionKeys = getWorkflowPermissionKeys(workflow.id);
                            const workflowModified = isRoleWorkflowModified(workflow);

                            return (
                              <Card
                                key={`role-workflow-${workflow.id}`}
                                className={`border shadow-sm transition-colors ${
                                  workflowModified
                                    ? "border-amber-300 bg-amber-50/50 ring-1 ring-amber-100"
                                    : "border-gray-200 bg-white"
                                }`}
                              >
                                <CardHeader className="pb-3">
                                  <div className="flex items-start gap-3">
                                    <div
                                      className={`rounded-lg p-2 ${
                                        workflowModified
                                          ? "bg-amber-100 text-amber-700"
                                          : "bg-blue-50 text-blue-600"
                                      }`}
                                    >
                                      <Icon className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <CardTitle className="text-base">
                                          {workflow.title}
                                        </CardTitle>
                                        {workflowModified ? (
                                          <Badge
                                            variant="outline"
                                            className="border-amber-200 bg-amber-100 text-[11px] font-semibold text-amber-800"
                                          >
                                            Modified
                                          </Badge>
                                        ) : null}
                                      </div>
                                      <p className="mt-1 text-sm text-gray-500">
                                        {workflow.description}
                                      </p>
                                    </div>
                                    <Badge
                                      variant="outline"
                                      className="shrink-0 border-blue-100 bg-blue-50 text-[11px] font-semibold text-blue-700"
                                    >
                                      {workflowLabel}
                                    </Badge>
                                  </div>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                  <InlineAccessPicker
                                    id={`role.workflow.${workflow.id}`}
                                    value={workflowValue}
                                    options={workflow.options}
                                    disabled={isRoleReadOnly}
                                    openPickerId={openAccessPickerId}
                                    setOpenPickerId={setOpenAccessPickerId}
                                    onChange={(nextValue) =>
                                      handleRoleWorkflowChange(workflow, nextValue)
                                    }
                                  />
                                  {isCustomWorkflowValue ? (
                                    <p className="text-xs font-medium text-amber-700">
                                      {CUSTOM_WORKFLOW_ACCESS_HELPER_TEXT}
                                    </p>
                                  ) : null}

                                  <AccessDefaultSummary
                                    defaultLevel={originalWorkflowLabel}
                                    currentLevel={workflowLabel}
                                    isCustom={workflowModified}
                                  />

                                  {workflow.id === "postCallDocumentation" ? (
                                    <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Includes
                                      </div>
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {POST_CALL_INCLUDES.map((item) => (
                                          <Badge
                                            key={item}
                                            variant="outline"
                                            className="bg-white text-gray-700"
                                          >
                                            {item}
                                          </Badge>
                                        ))}
                                      </div>
                                      {/* TODO: Add patient portal permission support when Patient Portal / Send to Patient permissions are introduced. */}
                                    </div>
                                  ) : null}

                                  <div className="rounded-lg border border-gray-200 bg-white">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        toggleRoleWorkflowAdvanced(workflow.id)
                                      }
                                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm font-medium text-gray-900"
                                    >
                                      Advanced permissions
                                      {isAdvancedOpen ? (
                                        <ChevronDown className="h-4 w-4 text-gray-500" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-gray-500" />
                                      )}
                                    </button>
                                    {isAdvancedOpen ? (
                                      <div className="space-y-3 border-t border-gray-100 p-3">
                                        {permissionKeys.map((permissionKey) => {
                                          const permissionModified =
                                            isRolePermissionModified(permissionKey);

                                          return (
                                            <div
                                              key={`role-${workflow.id}-${permissionKey}`}
                                              className={`flex flex-col gap-3 rounded-lg border p-3 lg:flex-row lg:items-center lg:justify-between ${
                                                permissionModified
                                                  ? "border-amber-200 bg-amber-50"
                                                  : "border-gray-100 bg-gray-50"
                                              }`}
                                            >
                                              <div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                  <div className="text-sm font-medium text-gray-900">
                                                    {formatPermissionLabel(permissionKey)}
                                                  </div>
                                                  {permissionModified ? (
                                                    <Badge
                                                      variant="outline"
                                                      className="border-amber-200 bg-amber-100 text-[10px] font-semibold text-amber-800"
                                                    >
                                                      Modified
                                                    </Badge>
                                                  ) : null}
                                                </div>
                                                <div className="mt-1 text-xs text-gray-500">
                                                  {permissionKey}
                                                </div>
                                              </div>
                                              <div className="flex justify-end">
                                                <PermissionSelector
                                                  value={
                                                    roleForm.permissions?.[
                                                      permissionKey
                                                    ] || ""
                                                  }
                                                  onChange={(val) =>
                                                    handleRolePermissionChange(
                                                      permissionKey,
                                                      val
                                                    )
                                                  }
                                                  disabled={isRoleReadOnly}
                                                  options={getOverrideOptionsForPermission(
                                                    permissionKey
                                                  )}
                                                />
                                              </div>
                                            </div>
                                          );
                                        })}

                                        {workflow.id === "postCallDocumentation" ? (
                                          <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-3">
                                            <div className="mb-3 text-sm font-semibold text-blue-950">
                                              Advanced Controls for EHR submission
                                            </div>
                                            <div className="space-y-3">
                                              {EHR_ADVANCED_CONTROLS.map((control) => {
                                                const permissionValue = getPermissionLevel(
                                                  roleForm.permissions,
                                                  control.permissionKey
                                                );
                                                const permissionModified =
                                                  isRolePermissionModified(
                                                    control.permissionKey
                                                  );
                                                const ehrValue =
                                                  permissionValue === "write" &&
                                                  workflowValue === "submit"
                                                    ? "submit"
                                                    : permissionValue;

                                                return (
                                                  <div
                                                    key={`role-ehr-${control.id}`}
                                                    className={`grid grid-cols-1 gap-2 rounded-lg border p-3 md:grid-cols-[170px_minmax(0,1fr)] md:items-center ${
                                                      permissionModified
                                                        ? "border-amber-200 bg-amber-50"
                                                        : "border-transparent bg-transparent"
                                                    }`}
                                                  >
                                                    <div>
                                                      <div className="flex flex-wrap items-center gap-2">
                                                        <span className="text-sm font-medium text-gray-700">
                                                          {control.label}
                                                        </span>
                                                        {permissionModified ? (
                                                          <Badge
                                                            variant="outline"
                                                            className="border-amber-200 bg-amber-100 text-[10px] font-semibold text-amber-800"
                                                          >
                                                            Modified
                                                          </Badge>
                                                        ) : null}
                                                      </div>
                                                      <div className="mt-1 text-xs text-gray-500">
                                                        {control.permissionKey}
                                                      </div>
                                                    </div>
                                                    <InlineAccessPicker
                                                      id={`role.ehr.${control.permissionKey}`}
                                                      value={ehrValue}
                                                      options={EHR_CONTROL_OPTIONS}
                                                      disabled={isRoleReadOnly}
                                                      openPickerId={openAccessPickerId}
                                                      setOpenPickerId={
                                                        setOpenAccessPickerId
                                                      }
                                                      onChange={(nextValue) =>
                                                        handleRolePermissionChange(
                                                          control.permissionKey,
                                                          nextValue === "submit"
                                                            ? "write"
                                                            : nextValue
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      </div>

                      {!isRoleReadOnly && hasUnsavedRoleChanges ? (
                        <div className="sticky bottom-5 z-30 w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-[0_18px_45px_rgba(15,23,42,0.16)] ring-1 ring-white/80">
                          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5 rounded-full bg-amber-50 p-2 text-amber-600 ring-1 ring-amber-100">
                                <AlertTriangle className="h-4 w-4" />
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-gray-900">
                                  You have unsaved changes to this role.
                                </div>
                                <div className="mt-0.5 text-xs text-gray-600">
                                  Save these role updates or discard them before switching context.
                                </div>
                              </div>
                            </div>
                            <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={discardRoleChanges}
                                disabled={savingRole}
                                className="border-gray-300 bg-white"
                              >
                                Discard Changes
                              </Button>
                              <Button
                                type="button"
                                onClick={saveRoleDefinition}
                                disabled={savingRole}
                                className="bg-blue-600 text-white hover:bg-blue-700"
                              >
                                {savingRole ? "Saving..." : "Save Role"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {!isRoleReadOnly ? (
                        <div className="flex flex-wrap justify-end gap-3">
                          <Button type="button" variant="outline" onClick={cancelRoleEditor}>
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            onClick={saveRoleDefinition}
                            disabled={savingRole}
                            className="bg-blue-600 text-white hover:bg-blue-700"
                          >
                            {savingRole
                              ? "Saving..."
                              : editorMode === "create"
                              ? "Create Role"
                              : "Save Changes"}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
      ) : activeTab === "approvals" ? (
        <div className="space-y-4" data-tour="admin-approvals-list">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">User Requests</h2>
            <p className="mt-1 text-sm text-gray-500">
              Review pending access requests and completed approval decisions.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card className="border border-gray-200 shadow-sm">
              <CardContent className="flex items-center gap-3 p-3">
                <div className="rounded-full bg-amber-50 p-3 text-amber-600">
                  <Users className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Pending
                  </div>
                  <div className="text-2xl font-semibold text-gray-900">
                    {pendingApprovals.length}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm">
              <CardContent className="flex items-center gap-3 p-3">
                <div className="rounded-full bg-emerald-50 p-3 text-emerald-600">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Approved
                  </div>
                  <div className="text-2xl font-semibold text-gray-900">
                    {users.filter((user) => user.approvalStatus === "approved").length}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm">
              <CardContent className="flex items-center gap-3 p-3">
                <div className="rounded-full bg-red-50 p-3 text-red-600">
                  <SlidersHorizontal className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Rejected
                  </div>
                  <div className="text-2xl font-semibold text-gray-900">
                    {users.filter((user) => user.approvalStatus === "rejected").length}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border border-gray-200 shadow-sm">
            <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-[minmax(0,1fr)_220px_auto] md:items-center">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={requestSearch}
                  onChange={(event) => setRequestSearch(event.target.value)}
                  placeholder="Search requests by name, email, role, or reviewer"
                  className="pl-9"
                />
              </div>
              <select
                value={requestRoleFilter}
                onChange={(event) => setRequestRoleFilter(event.target.value)}
                className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All roles</option>
                {availableRoleOptions.map((roleName) => (
                  <option key={roleName} value={roleName}>
                    {roleName}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                onClick={refreshPendingApprovals}
                disabled={refreshingApprovals}
                className="justify-center"
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${refreshingApprovals ? "animate-spin" : ""}`}
                />
                {refreshingApprovals ? "Refreshing..." : "Refresh"}
              </Button>
            </CardContent>
          </Card>

          {approvalsError ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              {approvalsError}
            </div>
          ) : null}

          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Pending Approvals</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-sm text-gray-500">Loading pending approvals...</div>
              ) : filteredUserRequests.pending.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
                  No pending approvals match the current filters.
                </div>
              ) : (
                true ? (
                <div className="overflow-hidden rounded-lg border border-gray-100">
                  <div className="overflow-x-auto">
                    <table className="min-w-[900px] table-fixed divide-y divide-gray-100">
                      <colgroup>
                        <col className="w-[22%]" />
                        <col className="w-[28%]" />
                        <col className="w-[16%]" />
                        <col className="w-[14%]" />
                        <col className="w-[20%]" />
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Email</th>
                          <th className="px-4 py-3">Role</th>
                          <th className="px-4 py-3">Requested On</th>
                          <th className="px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {filteredUserRequests.pending.map((user) => (
                          <tr
                            key={user.userId || user.id}
                            className="transition-colors hover:bg-blue-50/40"
                          >
                            <td className="px-4 py-4 align-middle">
                              <RequestNameCell user={user} />
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <TruncatedRequestCell value={getUserEmail(user)} />
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <RequestRoleBadge role={getUserRole(user)} />
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <RequestDateCell
                                value={user.createdAt || user.created_at || user.updatedAt}
                              />
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setApprovalPendingReject(user)}
                                  disabled={
                                    !canWriteAdminSettings ||
                                    approvingUserId === user.userId ||
                                    rejectingUserId === user.userId
                                  }
                                  className="border-red-200 bg-white text-red-600 hover:bg-red-50 hover:text-red-700"
                                >
                                  {rejectingUserId === user.userId ? "Rejecting..." : "Reject"}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => handleApprovePendingUser(user)}
                                  disabled={
                                    !canWriteAdminSettings ||
                                    approvingUserId === user.userId ||
                                    rejectingUserId === user.userId
                                  }
                                  className="bg-blue-600 text-white hover:bg-blue-700"
                                >
                                  {approvingUserId === user.userId ? "Approving..." : "Approve"}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[860px]">
                    <div className="grid grid-cols-[1.2fr_1.4fr_1fr_1fr_220px] gap-3 border-b border-gray-200 px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <div>Name</div>
                      <div>Email</div>
                      <div>Role</div>
                      <div>Requested On</div>
                      <div>Actions</div>
                    </div>
                    {filteredUserRequests.pending.map((user) => (
                      <div
                        key={user.userId || user.id}
                        className="grid grid-cols-[1.2fr_1.4fr_1fr_1fr_220px] gap-3 border-b border-gray-100 px-3 py-3 text-sm last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{getDisplayName(user)}</div>
                        <div className="text-gray-600">{getUserEmail(user) || "—"}</div>
                        <div className="text-gray-600">{getUserRole(user)}</div>
                        <div className="text-gray-600">
                          {formatDateTime(user.createdAt || user.created_at || user.updatedAt)}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setApprovalPendingReject(user)}
                            disabled={
                              !canWriteAdminSettings ||
                              approvingUserId === user.userId ||
                              rejectingUserId === user.userId
                            }
                            className="border-red-200 bg-white text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            {rejectingUserId === user.userId ? "Rejecting..." : "Reject"}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleApprovePendingUser(user)}
                            disabled={
                              !canWriteAdminSettings ||
                              approvingUserId === user.userId ||
                              rejectingUserId === user.userId
                            }
                            className="bg-blue-600 text-white hover:bg-blue-700"
                          >
                            {approvingUserId === user.userId ? "Approving..." : "Approve"}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                )
              )}
            </CardContent>
          </Card>

          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Approved Users</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-sm text-gray-500">Loading approved users...</div>
              ) : filteredUserRequests.approved.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
                  No approved users match the current filters.
                </div>
              ) : (
                true ? (
                <div className="overflow-hidden rounded-lg border border-gray-100">
                  <div className="overflow-x-auto">
                    <table className="min-w-[1100px] table-fixed divide-y divide-gray-100">
                      <colgroup>
                        <col className="w-[18%]" />
                        <col className="w-[28%]" />
                        <col className="w-[14%]" />
                        <col className="w-[22%]" />
                        <col className="w-[9%]" />
                        <col className="w-[9%]" />
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Email</th>
                          <th className="px-4 py-3">Role</th>
                          <th className="px-4 py-3">Approved By</th>
                          <th className="px-4 py-3">Approved On</th>
                          <th className="px-4 py-3">Last Login</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {filteredUserRequests.approved.map((user) => {
                          const approvedBy = formatApproverName(user.approvedBy);
                          const isSystemApprover = user.approvedBy === "system-bootstrap";

                          return (
                            <tr
                              key={user.userId || user.id}
                              className="transition-colors hover:bg-blue-50/40"
                            >
                              <td className="px-4 py-4 align-middle">
                                <RequestNameCell user={user} />
                              </td>
                              <td className="px-4 py-4 align-middle">
                                <TruncatedRequestCell value={getUserEmail(user)} />
                              </td>
                              <td className="px-4 py-4 align-middle">
                                <RequestRoleBadge role={getUserRole(user)} />
                              </td>
                              <td className="px-4 py-4 align-middle">
                                <TruncatedRequestCell
                                  value={approvedBy}
                                  className={isSystemApprover ? "text-gray-500" : ""}
                                />
                              </td>
                              <td className="px-4 py-4 align-middle">
                                <RequestDateCell value={user.approvedAt} />
                              </td>
                              <td className="px-4 py-4 align-middle">
                                <RequestDateCell value={user.lastLoginAt} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[980px]">
                    <div className="grid grid-cols-[1.1fr_1.4fr_0.9fr_1.2fr_1fr_1fr] gap-3 border-b border-gray-200 px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <div>Name</div>
                      <div>Email</div>
                      <div>Role</div>
                      <div>Approved By</div>
                      <div>Approved On</div>
                      <div>Last Login</div>
                    </div>
                    {filteredUserRequests.approved.map((user) => (
                      <div
                        key={user.userId || user.id}
                        className="grid grid-cols-[1.1fr_1.4fr_0.9fr_1.2fr_1fr_1fr] gap-3 border-b border-gray-100 px-3 py-3 text-sm last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{getDisplayName(user)}</div>
                        <div className="text-gray-600">{getUserEmail(user) || "—"}</div>
                        <div className="text-gray-600">{getUserRole(user)}</div>
                        <div className="text-gray-600">{formatApproverName(user.approvedBy)}</div>
                        <div className="text-gray-600">{formatDateTime(user.approvedAt)}</div>
                        <div className="text-gray-600">{formatDateTime(user.lastLoginAt)}</div>
                      </div>
                    ))}
                  </div>
                </div>
                )
              )}
            </CardContent>
          </Card>

          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Rejected Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-sm text-gray-500">Loading rejected requests...</div>
              ) : filteredUserRequests.rejected.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
                  No rejected requests match the current filters.
                </div>
              ) : (
                true ? (
                <div className="overflow-hidden rounded-lg border border-gray-100">
                  <div className="overflow-x-auto">
                    <table className="min-w-[940px] table-fixed divide-y divide-gray-100">
                      <colgroup>
                        <col className="w-[20%]" />
                        <col className="w-[30%]" />
                        <col className="w-[16%]" />
                        <col className="w-[20%]" />
                        <col className="w-[14%]" />
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Email</th>
                          <th className="px-4 py-3">Role</th>
                          <th className="px-4 py-3">Rejected By</th>
                          <th className="px-4 py-3">Rejected On</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {filteredUserRequests.rejected.map((user) => (
                          <tr
                            key={user.userId || user.id}
                            className="transition-colors hover:bg-blue-50/40"
                          >
                            <td className="px-4 py-4 align-middle">
                              <RequestNameCell user={user} />
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <TruncatedRequestCell value={getUserEmail(user)} />
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <RequestRoleBadge role={getUserRole(user)} />
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <TruncatedRequestCell value={formatApproverName(user.rejectedBy)} />
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <RequestDateCell value={user.rejectedAt} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[820px]">
                    <div className="grid grid-cols-[1.2fr_1.5fr_1fr_1.2fr_1fr] gap-3 border-b border-gray-200 px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <div>Name</div>
                      <div>Email</div>
                      <div>Role</div>
                      <div>Rejected By</div>
                      <div>Rejected On</div>
                    </div>
                    {filteredUserRequests.rejected.map((user) => (
                      <div
                        key={user.userId || user.id}
                        className="grid grid-cols-[1.2fr_1.5fr_1fr_1.2fr_1fr] gap-3 border-b border-gray-100 px-3 py-3 text-sm last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{getDisplayName(user)}</div>
                        <div className="text-gray-600">{getUserEmail(user) || "—"}</div>
                        <div className="text-gray-600">{getUserRole(user)}</div>
                        <div className="text-gray-600">{formatApproverName(user.rejectedBy)}</div>
                        <div className="text-gray-600">{formatDateTime(user.rejectedAt)}</div>
                      </div>
                    ))}
                  </div>
                </div>
                )
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card className="border border-gray-200 shadow-sm">
              <CardContent className="flex items-center gap-3 p-3">
                <div className="rounded-full bg-blue-50 p-3 text-blue-600">
                  <Plus className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Pending Invitations
                  </div>
                  <div className="text-2xl font-semibold text-gray-900">
                    {invitations.length}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm">
              <CardContent className="flex items-center gap-3 p-3">
                <div className="rounded-full bg-emerald-50 p-3 text-emerald-600">
                  <BriefcaseBusiness className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Role Options
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {availableRoleOptions.length} active roles
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm">
              <CardContent className="flex items-center gap-3 p-3">
                <div className="rounded-full bg-amber-50 p-3 text-amber-600">
                  <Users className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Clinic
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {loggedInClinicName || "Current clinic"}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {invitationsError ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              {invitationsError}
            </div>
          ) : null}

          <Card className="border border-gray-200 shadow-sm" data-tour="admin-invite-form">
            <CardHeader>
              <CardTitle className="text-base">Invite Team Members</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_220px_auto]">
              <Input
                value={invitationForm.email}
                onChange={(event) =>
                  setInvitationForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                disabled={!canWriteAdminSettings}
                placeholder="teammate@example.com"
              />
              <select
                value={invitationForm.roleName}
                onChange={(event) =>
                  setInvitationForm((current) => ({
                    ...current,
                    roleName: event.target.value,
                  }))
                }
                disabled={!canWriteAdminSettings}
                className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose a role</option>
                {availableRoleOptions.map((roleName) => (
                  <option key={roleName} value={roleName}>
                    {roleName}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                onClick={handleSendInvitation}
                disabled={!canWriteAdminSettings || sendingInvitation}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                {sendingInvitation ? "Sending..." : "Send Invitation"}
              </Button>
            </CardContent>
          </Card>

          <Card className="border border-gray-200 shadow-sm" data-tour="admin-invites-pending">
            <CardHeader>
              <CardTitle className="text-base">Pending Invitations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="text-sm text-gray-500">Loading invitations...</div>
              ) : invitations.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
                  No active invitations have been sent yet.
                </div>
              ) : (
                invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="flex flex-col gap-4 rounded-xl border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {invitation.invitedEmail}
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        {invitation.roleName}
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        Sent: {formatDateTime(invitation.createdAt)}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleRevokeInvitation(invitation)}
                      disabled={!canWriteAdminSettings || revokingInvitationId === invitation.id}
                    >
                      {revokingInvitationId === invitation.id ? "Revoking..." : "Revoke"}
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      )}
      <MaxSeatModal open={showMaxSeatModal} onOpenChange={setShowMaxSeatModal} />
    </div>
  );
}

export default RBACManagement;
